import { useState } from 'react'
import type { ComponentType, ReactNode, SVGProps } from 'react'
import { Link } from 'react-router-dom'
import {
  HourglassIcon, BookIcon, ScrollIcon,
  GoldIcon, WoodIcon, RumIcon, PrestigeIcon,
  BuildingsIcon, BuildingIcon, AnchorIcon, ShipIcon, ModuleIcon,
  CompassIcon, MapIcon, CombatIcon, ForgeIcon, PennantIcon, TargetIcon, CrownIcon,
  StatIcon, type StatName,
  PortIcon, SkullIcon, MysteryIcon, TaleIcon, StormIcon, ReefIcon, MineIcon, IslandIcon,
  DefenseIcon, ChevronRight, ParrotIcon, TreasureMapIcon, BottleIcon,
  DiplomacyIcon, ScrollQuillIcon, LanternIcon, LifebuoyIcon, CabineIcon, BalanceIcon,
} from '@/components/game/GameIcons'
import { ResourceTally } from '@/components/game/ui'
import { SeaScene } from '@/components/game/SeaScene'
import { SeoHead } from '@/components/SeoHead'
import { ROUTE_SEO } from '@/seo'

/**
 * /codex — « Le Codex du corsaire » : le grand livre des règles et mécaniques.
 * Page publique (visible des visiteurs comme des capitaines), pensée pour être
 * parcourue : navigation par ancres, sections illustrées, encarts plutôt que
 * pavés de texte.
 *
 * ⚓ RÈGLE D'OR (voir CLAUDE.md) — toute fonctionnalité core ajoutée ou tout
 * élément de gameplay modifié DOIT être répercuté ici. Zéro désynchro tolérée
 * entre le Codex et le gameplay réel. Ne décrire que ce qui est réellement
 * implémenté ; flaguer « À venir » ce qui est planifié.
 */

type Icon = ComponentType<SVGProps<SVGSVGElement>>

const SECTIONS: { id: string; label: string; Icon: Icon }[] = [
  { id: 'principe', label: 'Le principe', Icon: HourglassIcon },
  { id: 'fin-de-saison', label: 'La fin de saison', Icon: BalanceIcon },
  { id: 'ressources', label: 'Ressources', Icon: GoldIcon },
  { id: 'batiments', label: 'Île & bâtiments', Icon: BuildingsIcon },
  { id: 'flotte', label: 'La flotte', Icon: AnchorIcon },
  { id: 'missions', label: 'Les missions', Icon: CompassIcon },
  { id: 'carte', label: 'La carte', Icon: MapIcon },
  { id: 'combat', label: 'Le combat', Icon: CombatIcon },
  { id: 'kraken', label: 'Chasse légendaire', Icon: StormIcon },
  { id: 'embuscades', label: 'Embuscades en mer', Icon: SkullIcon },
  { id: 'defense', label: 'La défense', Icon: DefenseShield },
  { id: 'reputation', label: 'Réputation', Icon: PennantIcon },
  { id: 'forge', label: 'La Forge', Icon: ForgeIcon },
  { id: 'chantier', label: 'Le Grand Chantier', Icon: LanternIcon },
  { id: 'objectifs', label: 'Les objectifs', Icon: ScrollIcon },
  { id: 'serie', label: 'Carte au trésor', Icon: LanternIcon },
  { id: 'taverne', label: 'La Taverne', Icon: (p) => <BuildingIcon type="tavern" {...p} /> },
  { id: 'quetes', label: 'Les quêtes', Icon: TreasureMapIcon },
  { id: 'prestige', label: 'Le prestige', Icon: PrestigeIcon },
  { id: 'notifications', label: 'Notifications', Icon: ParrotIcon },
  { id: 'pvp', label: 'Le PvP', Icon: TargetIcon },
  { id: 'recits', label: 'Les récits', Icon: ScrollQuillIcon },
  { id: 'classements', label: 'Classements', Icon: CrownIcon },
  { id: 'profil-public', label: 'Profil public', Icon: PennantIcon },
  { id: 'cabine', label: "La cabine du capitaine", Icon: CabineIcon },
]

export function Codex() {
  // Landing at the top on navigation is handled globally by useScrollToTop in
  // AppLayout (the footer link is anchorless → top reset). The in-page anchor
  // smooth-scroll below is preserved (hash navs are a no-op in that hook).
  return (
    <div className="relative">
      <SeoHead {...ROUTE_SEO['/codex']} />
      {/* smooth anchor scrolling while the Codex is mounted */}
      <style>{'html{scroll-behavior:smooth}'}</style>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 pb-[320px]">
        <Hero />
        <QuickNav />

        <div className="mt-10 space-y-14">
          <Principe />
          <FinDeSaison />
          <Ressources />
          <Batiments />
          <Flotte />
          <Missions />
          <Carte />
          <Recits />
          <Combat />
          <Kraken />
          <Embuscades />
          <Defense />
          <Reputation />
          <Forge />
          <GrandChantier />
          <Objectifs />
          <Serie />
          <Taverne />
          <Quetes />
          <Prestige />
          <Notifications />
          <Pvp />
          <Classements />
          <ProfilPublic />
          <Cabine />
        </div>

        <ClosingNote />
      </div>

      {/* signature maritime backdrop, like the Forge & Island surfaces */}
      <SeaScene scene="map" />
    </div>
  )
}

/* ──────────────────────────────── hero ──────────────────────────────── */

function Hero() {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/45 p-7 sm:p-9">
      <CompassWatermark />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
        <span className="grid place-items-center w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-2xl bg-ocean-900/60 border-2 border-sand-500/30 text-sand-300">
          <BookIcon className="w-10 h-10 sm:w-12 sm:h-12" />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.32em] text-ocean-300 mb-1.5">Corsalia · Règles &amp; mécaniques</p>
          <h1 className="font-display text-4xl sm:text-5xl text-sand-300 leading-none">Le Codex du corsaire</h1>
          <p className="mt-3 text-ocean-100 leading-relaxed max-w-2xl">
            Tout ce qu'un capitaine doit savoir avant de lever l'ancre : les ressources, les missions,
            le combat, la réputation et le reste. Lis dans l'ordre, ou saute droit à ce qui t'intéresse.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {/* Pas de numéro de saison ici : cette page est publique et
                pré-rendue (scripts/prerender.mjs), elle ne lit pas /api/me, donc
                un numéro en dur ne peut que devenir faux au premier reset. La
                saison vivante se lit en pied de page et en tête des classements,
                deux surfaces qui la tiennent du serveur. */}
            <Tag tone="gold">100&nbsp;% anti-pay-to-win</Tag>
            <Tag tone="plain">{SECTIONS.length} chapitres</Tag>
          </div>
        </div>
      </div>
    </section>
  )
}

function CompassWatermark() {
  return (
    <div className="pointer-events-none absolute -right-10 -top-12 w-56 h-56 opacity-[0.06]" aria-hidden="true">
      <svg viewBox="0 0 200 200" className="w-full h-full" fill="none">
        <g stroke="#E5CC8F"><circle cx="100" cy="100" r="92" strokeWidth="0.6" /><circle cx="100" cy="100" r="60" strokeWidth="0.5" /></g>
        {[0, 90, 180, 270].map((deg) => (
          <g key={deg} transform={`rotate(${deg} 100 100)`}>
            <path d="M100,16 L92,90 L100,100 Z" fill="#F5EDD7" />
            <path d="M100,16 L108,90 L100,100 Z" fill="#C9A55C" />
          </g>
        ))}
      </svg>
    </div>
  )
}

/* ──────────────────────────────── quick nav ──────────────────────────── */

function QuickNav() {
  // Desktop shows every chapter at once in a sticky wrapped panel. On mobile that
  // many chips can't fit, and a horizontal scroll row hid most of them with no
  // affordance — so mobile gets a collapsible "Sommaire" disclosure: a compact
  // sticky bar that expands into a 2-column grid and folds back on pick.
  const [open, setOpen] = useState(false)

  return (
    <nav
      aria-label="Sommaire du Codex"
      className="mt-6 sticky top-16 z-20 rounded-xl border border-sand-500/15 bg-ocean-900/85 backdrop-blur-md px-2 py-2"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="codex-toc"
        className="md:hidden flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1 text-ocean-100 hover:text-sand-200 transition-colors"
      >
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em]">
          <BookIcon className="w-3.5 h-3.5 text-sand-300" />
          Sommaire · {SECTIONS.length} chapitres
        </span>
        <ChevronRight className={`w-4 h-4 text-sand-300 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      <ul
        id="codex-toc"
        className={`${open ? 'grid' : 'hidden'} grid-cols-2 gap-2 mt-2 md:mt-0 md:flex md:flex-wrap`}
      >
        {SECTIONS.map(({ id, label, Icon }) => (
          <li key={id} className="min-w-0 md:shrink-0">
            <a
              href={`#${id}`}
              onClick={() => setOpen(false)}
              className="flex md:inline-flex items-center gap-1.5 rounded-full border border-sand-500/20 bg-ocean-900/40 px-3 py-1.5 text-xs text-ocean-100 hover:border-sand-300/50 hover:text-sand-200 transition-colors md:whitespace-nowrap"
            >
              <Icon className="w-3.5 h-3.5 text-sand-300 shrink-0" />
              <span className="truncate">{label}</span>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}

/* ──────────────────────────────── sections ───────────────────────────── */

function Section({
  id, Icon, title, lead, wip, children,
}: { id: string; Icon: Icon; title: string; lead?: ReactNode; wip?: boolean; children: ReactNode }) {
  const idx = SECTIONS.findIndex((s) => s.id === id)
  const n = String((idx < 0 ? 0 : idx) + 1).padStart(2, '0')
  return (
    <section id={id} className="scroll-mt-32">
      <div className="flex items-start gap-4">
        <span className="grid place-items-center w-12 h-12 shrink-0 rounded-xl bg-ocean-900/50 border border-sand-500/25 text-sand-300">
          <Icon className="w-6 h-6" />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.28em] text-ocean-300 flex items-center gap-2">
            Chapitre {n}{wip && <WipTag />}
          </p>
          <h2 className="font-display text-2xl sm:text-3xl text-sand-300 leading-tight">{title}</h2>
        </div>
      </div>
      {lead && <p className="mt-3 text-ocean-100 leading-relaxed max-w-3xl">{lead}</p>}
      <div className="mt-5 space-y-4">{children}</div>
    </section>
  )
}

function Principe() {
  return (
    <Section
      id="principe"
      Icon={HourglassIcon}
      title="Un jeu qui se joue à ton rythme"
      lead={
        <>
          Corsalia est un <strong className="text-sand-200">jeu de gestion maritime asynchrone</strong>, sans
          pay-to-win : aucune mécanique ne te demande de rester connecté. Tu donnes des ordres (construire,
          explorer, commercer, attaquer), puis tu fermes l'onglet. Le temps fait le reste.
        </>
      }
    >
      <div className="grid sm:grid-cols-3 gap-4">
        <Mini Icon={HourglassIcon} title="Le temps joue pour toi">
          Tes ressources montent toutes seules, tes navires voguent et tes chantiers s'achèvent à l'heure dite,
          que tu sois là ou non.
        </Mini>
        <Mini Icon={CompassIcon} title="Tu lances, ça se résout">
          Une mission part avec un compte à rebours. À l'échéance, le serveur en calcule l'issue et te prévient
          en temps réel.
        </Mini>
        <Mini Icon={AnchorIcon} title="Reviens quand tu veux">
          Cinq minutes ou cinq heures plus tard : tu retrouves le butin rentré, la flotte au port et la
          prochaine décision à prendre.
        </Mini>
      </div>
      <Note tone="info" Icon={HourglassIcon} title="Une saison a une fin, et tu la vois venir">
        Le jeu se joue par saisons, et chacune a son terme, fixé à son ouverture. Au bout, les tables de jeu
        repartent de zéro, et ton compte garde tout ce qu'il a gagné. Tu n'auras pas à deviner la date : dans
        les deux dernières semaines, un sablier s'allume dans la barre du haut. Le détail de ce qui repart et
        de ce qui reste tient dans son propre chapitre,{' '}
        <Link to="/codex#fin-de-saison" className="text-sand-300 underline underline-offset-4 hover:text-sand-100">la fin de saison</Link>.
      </Note>
      <Note tone="gold" Icon={PrestigeIcon} title="Une règle gravée dans la coque : anti-pay-to-win">
        L'abonnement premium n'achète <strong className="text-sand-200">que du confort et du cosmétique</strong>,
        jamais un bonus de statistiques, de butin ou de vitesse. Sur la mer, tout le monde est à armes égales.
      </Note>
    </Section>
  )
}

/**
 * La fin de saison, en chapitre à part.
 *
 * Elle vivait dispersée sur une dizaine de mentions (le principe, la réputation,
 * le Grand Chantier, les objectifs, le prestige, les classements), et c'est cette
 * dispersion qui a permis à un billet de promettre une « réputation de pionnier »
 * qui suit le joueur quand le cadran de réputation, lui, repart de zéro. Un
 * capitaine qui veut savoir ce que le couperet lui prend doit le lire en un seul
 * endroit. Le panneau du sablier de la barre renvoie ici.
 */
function FinDeSaison() {
  return (
    <Section
      id="fin-de-saison"
      Icon={BalanceIcon}
      title="Ce que la fin de saison prend, et ce qu'elle laisse"
      lead={
        <>
          Corsalia se joue par <strong className="text-sand-200">saisons</strong>, et chacune a un terme fixé à
          son ouverture. Au bout, les tables de jeu repartent de zéro pour tout le monde en même temps, et ton
          compte garde ce qu'il a gagné. C'est le seul moment du jeu où l'on te reprend quelque chose, donc voici
          exactement quoi.
        </>
      }
    >
      <div className="grid sm:grid-cols-3 gap-4">
        <Mini Icon={HourglassIcon} title="Tu vois la date venir">
          Dans les deux dernières semaines, un sablier s'allume dans la barre du haut et affiche les jours qui
          restent, en ambre sur la dernière semaine. Ouvre-le : il te donne la semaine en cours et l'avancement.
          Le nom de la saison et la semaine se lisent aussi en pied de page et en tête des classements, à toute
          heure.
        </Mini>
        <Mini Icon={AnchorIcon} title="Tout le monde relève l'ancre">
          Personne ne prend d'avance : la remise à zéro est simultanée, et ton île change de place et de nom, le
          tirage dépendant de la saison autant que de toi.
        </Mini>
        <Mini Icon={PrestigeIcon} title="Rien de gagné ne s'efface">
          Ton compte, ton prestige, tes titres et ta cabine traversent les saisons. Aucun point déjà gagné n'a
          jamais été retiré à personne.
        </Mini>
      </div>

      <Note tone="danger" Icon={BalanceIcon} title="Ce qui repart de zéro">
        Ton <strong className="text-sand-200">île</strong> et ses bâtiments, tes{' '}
        <strong className="text-sand-200">réserves</strong>, ta <strong className="text-sand-200">flotte</strong>{' '}
        et ses modules, tes missions en cours, tes quêtes, tes récits, ton arbre de{' '}
        <Link to="/codex#forge" className="text-sand-300 underline underline-offset-4 hover:text-sand-100">Forge</Link>,
        ton <Link to="/codex#chantier" className="text-sand-300 underline underline-offset-4 hover:text-sand-100">Grand Phare</Link>,
        tes objectifs de saison, ta série de connexions, et ton{' '}
        <Link to="/codex#reputation" className="text-sand-300 underline underline-offset-4 hover:text-sand-100">cadran de réputation</Link>{' '}
        : ton pavillon et ton rang de notoriété se rejouent donc eux aussi, à égalité pour tout le monde.
        <p className="mt-2">
          Tu repars sur une île neuve : sept bâtiments au niveau 1, Quartier-Général compris, un brick, et un
          coffre de départ de <ResourceTally gold={200} wood={400} rhum={300} className="text-sand-200" />.
          La Taverne, elle, reste à construire.
        </p>
      </Note>

      <Note tone="gold" Icon={PrestigeIcon} title="Ce qui reste gravé sur ton compte">
        Ton <strong className="text-sand-200">prestige</strong>, tous tes{' '}
        <strong className="text-sand-200">titres</strong>, ta{' '}
        <Link to="/codex#cabine" className="text-sand-300 underline underline-offset-4 hover:text-sand-100">cabine</Link>{' '}
        entière (portrait, blason, cadre et titre équipé), tes titres de carte au trésor, et le palmarès des
        saisons déjà closes. Le tableau des <strong className="text-sand-200">Légendes</strong> ne se réinitialise
        jamais : c'est le seul des classements qui traverse les saisons et continue de cumuler.
      </Note>

      <Note tone="info" Icon={CrownIcon} title="À la clôture, le podium se grave">
        Les trois premiers de chacun des quatre tableaux saisonniers sont archivés et repartent avec un{' '}
        <strong className="text-sand-200">titre de podium</strong> qui porte le nom de la saison, introuvable
        ailleurs et plus jamais attribué à personne. Une place que personne n'a disputée reste vacante plutôt
        qu'attribuée par défaut. Tout se relit ensuite sur les{' '}
        <Link to="/codex#classements" className="text-sand-300 underline underline-offset-4 hover:text-sand-100">classements</Link>,
        à l'onglet des saisons passées.
      </Note>

      <Note tone="info" Icon={ScrollIcon} title="Deux règles qui surprennent, et il vaut mieux les savoir avant">
        Tes <Link to="/codex#objectifs" className="text-sand-300 underline underline-offset-4 hover:text-sand-100">faits d'armes</Link>{' '}
        de saison se décochent et se rejouent, et ils te repaient <strong className="text-sand-200">tout</strong> :
        leurs ressources et leur prestige. Ton renom monte donc d'une saison à l'autre, à hauteur du travail que tu
        refais. Dans une même saison, en revanche, un fait d'armes ne se réclame{' '}
        <strong className="text-sand-200">qu'une fois</strong>. Trois faits d'armes rares échappent à la règle et
        restent cochés à vie : ceux-là ne se rejouent pas, donc ne repaient rien.
        <p className="mt-2">
          Et le <Link to="/codex#chantier" className="text-sand-300 underline underline-offset-4 hover:text-sand-100">Grand Phare</Link>{' '}
          se referme sur le calendrier avant la fin de la saison : un palier dont la maçonnerie ne peut plus
          s'achever à temps n'accepte plus de versement, et rien n'est rendu. N'ouvre pas un palier dans les
          derniers jours.
        </p>
      </Note>
    </Section>
  )
}

function Ressources() {
  return (
    <Section
      id="ressources"
      Icon={GoldIcon}
      title="Trois ressources, jamais à ramasser"
      lead={
        <>
          Tout repose sur trois ressources qui s'accumulent seules sur ton île. Inutile de cliquer pour récolter :
          on calcule ce que tu as produit au moment où tu regardes (dernier relevé + production × temps écoulé).
        </>
      }
    >
      <div className="grid sm:grid-cols-3 gap-4">
        <ResourceCard Icon={GoldIcon} name="Or" sub="La monnaie universelle">
          Paie les bâtiments, les coques, les modules, la Forge et l'achat de marchandises au port. C'est ce
          que rapporte, au bout du compte, chaque mission.
        </ResourceCard>
        <ResourceCard Icon={WoodIcon} name="Bois" sub="Le matériau de masse">
          La charpente : bâtiments, coques de navires et modules. On peut en acheter avec de l'or au comptoir
          ou dans une scierie.
        </ResourceCard>
        <ResourceCard Icon={RumIcon} name="Rhum" sub="La ressource stratégique">
          Recrute l'équipage à la construction, nourrit la troupe à chaque voyage (rations), et alimente la
          Forge. Rare et précieux. On peut en acheter avec de l'or au comptoir ou dans une distillerie, mais
          jamais à bas prix.
        </ResourceCard>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="info" Icon={(p) => <BuildingIcon type="warehouse" {...p} />} title="L'entrepôt fixe le plafond">
          Chaque ressource est plafonnée séparément par la capacité de ton entrepôt. Au-delà, le surplus est
          perdu ; agrandis l'entrepôt ou dépense avant de déborder. Un butin qui déborde au retour d'une
          mission est « perdu en mer » : le rapport de mission te dit exactement combien.
        </Note>
        <Note tone="gold" Icon={AnchorIcon} title="Ton coffre de départ">
          <span className="inline-flex items-center gap-2">
            Tout capitaine débute avec <ResourceTally gold={200} wood={400} rhum={300} className="text-sand-200" />.
          </span>
        </Note>
      </div>
    </Section>
  )
}

function Batiments() {
  const buildings: { type: string; name: string; role: string }[] = [
    { type: 'hq', name: 'Quartier-général', role: "Le cœur du repaire : il fixe le niveau maximum de tous les autres bâtiments, et ouvre tes créneaux de chantier." },
    { type: 'gold_mine', name: "Mine d'or", role: "Produit de l'or, heure après heure." },
    { type: 'sawmill', name: 'Scierie', role: 'Produit du bois en continu.' },
    { type: 'distillery', name: 'Distillerie', role: 'Distille le rhum, le nerf de la guerre.' },
    { type: 'warehouse', name: 'Entrepôt', role: 'Augmente la capacité de stockage de chaque ressource.' },
    { type: 'shipyard', name: 'Chantier naval', role: "Construit les navires (de plus en plus en parallèle à mesure qu'il monte) et conditionne la taille de tes flottes de raid." },
    { type: 'capitainerie', name: 'Capitainerie', role: 'Ses mouillages fixent la taille maximale de ta flotte : cinq par niveau.' },
  ]
  return (
    <Section
      id="batiments"
      Icon={BuildingsIcon}
      title="Ton île et ses sept bâtiments"
      lead="Tu bâtis ton repaire en montant sept bâtiments de niveau en niveau. Trois produisent, un stocke, un construit les navires, un fixe tes mouillages. Et le quartier-général commande l'ensemble."
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {buildings.map((b) => (
          <Mini key={b.type} Icon={(p) => <BuildingIcon type={b.type} {...p} />} title={b.name}>
            {b.role}
          </Mini>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="info" Icon={(p) => <BuildingIcon type="hq" {...p} />} title="Le QG ouvre la voie">
          Un bâtiment ne peut dépasser le niveau du QG + 2. Pour pousser plus haut, monte d'abord ton
          quartier-général. Tu ne mènes qu'un seul chantier à la fois, jusqu'au jour où ton QG t'en
          ouvre un second.
        </Note>
        <Note tone="gold" Icon={(p) => <BuildingIcon type="hq" {...p} />} title="Les créneaux de chantier">
          À partir du <strong className="text-sand-200">niveau 18</strong> de quartier-général, tu ouvres un
          deuxième créneau de chantier, puis un de plus tous les deux niveaux. Deux créneaux, ce sont deux
          bâtiments qui montent en même temps. Mais un chantier, c'est une équipe : ouvrir un front
          supplémentaire coûte <strong className="text-sand-200">moitié plus cher</strong> que le tarif
          normal (et le double au troisième). Les durées, elles, ne bougent pas d'une seconde ; ce qui
          change, c'est le nombre de fronts que ta bourse peut nourrir. Un même bâtiment ne peut pas occuper
          deux créneaux : les créneaux servent à mener plusieurs chantiers de front, pas à empiler des
          niveaux d'avance. Construire un bâtiment neuf compte comme un chantier, tout comme l'améliorer.
        </Note>
        <Note tone="gold" Icon={GoldIcon} title="Une économie qui reste tendue">
          La production grimpe plus lentement que les coûts : impossible de « rouler sur l'or ». Une flotte
          active garde toujours ton trésor sous tension ; c'est voulu.
        </Note>
      </div>
    </Section>
  )
}

function Flotte() {
  const ships: { type: string; name: string; man: number; bor: number; abo: number; arm: number; hp: number; cale: number; eq: number }[] = [
    { type: 'brick', name: 'Brick', man: 3, bor: 2, abo: 2, arm: 1, hp: 3, cale: 75, eq: 5 },
    { type: 'fregate', name: 'Frégate', man: 2, bor: 5, abo: 4, arm: 2, hp: 6, cale: 150, eq: 15 },
    { type: 'galion', name: 'Galion', man: 1, bor: 9, abo: 7, arm: 3, hp: 10, cale: 375, eq: 40 },
    { type: 'legendary', name: 'Navire Légendaire', man: 4, bor: 16, abo: 12, arm: 5, hp: 24, cale: 600, eq: 30 },
  ]
  const cols: { key: StatName; label: string }[] = [
    { key: 'manoeuvre', label: 'Manœuvre' },
    { key: 'bordee', label: 'Bordée' },
    { key: 'abordage', label: 'Abordage' },
    { key: 'armure', label: 'Armure' },
    { key: 'hp', label: 'Coque' },
    { key: 'cargo', label: 'Cale' },
    { key: 'crew', label: 'Équipage' },
  ]
  return (
    <Section
      id="flotte"
      Icon={AnchorIcon}
      title="La flotte et le chantier"
      lead="Du modeste brick au galion blindé, chaque coque a son rôle. Le chantier naval débloque les types de navires plus puissants au fil de ses niveaux."
    >
      <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-2 overflow-x-auto">
        <table className="w-full min-w-[44rem] text-sm">
          <thead>
            <tr className="text-ocean-300">
              <th className="text-left font-normal px-3 py-2">Navire</th>
              {cols.map((c) => (
                <th key={c.key} className="px-2 py-2 font-normal">
                  <span className="inline-flex flex-col items-center gap-1">
                    <StatIcon name={c.key} className="w-4 h-4 text-sand-300" />
                    <span className="text-[11px]">{c.label}</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-sand-500/10">
            {ships.map((s) => (
              <tr key={s.type}>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-2 text-sand-200 font-medium">
                    <ShipIcon type={s.type} className="w-5 h-5 text-sand-300" />
                    {s.name}
                  </span>
                </td>
                {[s.man, s.bor, s.abo, s.arm, s.hp, s.cale, s.eq].map((v, i) => (
                  <td key={i} className="px-2 py-2.5 text-center tabular-nums text-ocean-100">{v}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Note tone="info" Icon={RumIcon} title="La cale nourrit, l'équipage dévore">
        Les deux dernières colonnes ne servent pas au combat, mais décident de ce qu'une sortie te rapporte
        vraiment. La <strong className="text-sand-200">cale</strong> est ce que le navire peut ramener ;
        l'<strong className="text-sand-200">équipage</strong> est ce qu'il coûte à nourrir, puisque les rations
        de rhum valent équipage × heures de mer. Attention au piège : le nombre de marins ne suit pas la taille
        de la coque. Un <strong className="text-sand-200">galion</strong> embarque 40 hommes pour 375 de cale,
        un <strong className="text-sand-200">Navire Légendaire</strong> seulement 30 pour 600. Le vaisseau-amiral
        rapporte donc deux fois plus par tonneau de rhum brûlé, alors que le galion est la coque la plus chère à
        faire naviguer du jeu. C'est un navire de ligne, pas un porteur.
      </Note>
      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="info" Icon={ModuleIcon} title="Les modules : déblocage à la Forge">
          Débloque un module en recherchant sa <strong className="text-sand-200">recette</strong> à la Forge ; le{' '}
          <strong className="text-sand-200">Faux-fond</strong>, lui, s'ouvre par la{' '}
          <strong className="text-sand-200">réputation</strong> (rang Notoire chez les Corsaires ou Pirates) et se
          re-mérite à chaque saison, comme la réputation qui le paie. Une fois
          débloqué, un module est disponible <strong className="text-sand-200">sans limite</strong> : tu l'équipes sur
          autant de navires que tu veux. Chaque coque a un <strong className="text-sand-200">nombre de slots</strong>{' '}
          (Brick 2, Frégate 3, Galion 4, Légendaire 5) que tu remplis <strong className="text-sand-200">comme tu
          veux</strong> : n'importe quel module, en n'importe quelle quantité. À toi de choisir l'identité du navire :
          coque <strong className="text-sand-200">pure combat</strong> (glass cannon),{' '}
          <strong className="text-sand-200">pure cargo</strong>, ou <strong className="text-sand-200">support</strong>.
          Les effets : un module <strong className="text-sand-200">combat</strong> ajoute +1 dé sur une phase (Bordée /
          Abordage / Manœuvre) ; la <strong className="text-sand-200">Cale renforcée</strong> +20&nbsp;% de soute ; le{' '}
          <strong className="text-sand-200">Gréement de course</strong> +15&nbsp;% de vitesse ; la{' '}
          <strong className="text-sand-200">Longue-vue</strong> augmente la reconnaissance.{' '}
          <strong className="text-sand-200">Tout s'empile</strong> jusqu'au nombre de slots : 4&nbsp;× Canons longs =
          +4 dés de Bordée, 2&nbsp;× Cale renforcée = +40&nbsp;% de soute, ou un Galion tout en{' '}
          <strong className="text-sand-200">Longue-vue</strong> pour un éclaireur d'élite. Un slot plein se{' '}
          <strong className="text-sand-200">remplace</strong> (swap). Attention au{' '}
          <strong className="text-sand-200">Faux-fond</strong> : malgré son nom, il
          n'agrandit pas la cale ; il améliore les <strong className="text-sand-200">prix du trafic</strong>, par
          exemplaire : +20&nbsp;% d'<GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> à la revente en
          contrebande, et jusqu'à −20&nbsp;% sur l'achat au marché noir,{' '}
          <strong className="text-sand-200">à la mesure de ta notoriété hors-la-loi</strong> (un capitaine dans la
          légalité n'a pas de remise). Et en convoi, seul le{' '}
          <strong className="text-sand-200">meilleur navire</strong> donne son taux à toute la cargaison : empiler sur
          une même coque cumule, répartir sur plusieurs navires n'apporte rien. <strong className="text-sand-200">Équiper</strong>{' '}
          coûte de l'<GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> or et du{' '}
          <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" /> bois (selon la taille de la coque) et prend du
          temps : le navire reste <strong className="text-sand-200">bloqué à quai</strong> (suivi dans le tableau de
          bord). <strong className="text-sand-200">Retirer</strong> ne coûte que du temps. Un navire en refonte reste
          aussi vulnérable qu'à quai si on te raid, et un navire coulé{' '}
          <strong className="text-sand-200">perd ses modules</strong>.
        </Note>
        <Note tone="info" Icon={PennantIcon} title="L'allure du convoi : le plus lent commande">
          Une flotte ne va <strong className="text-sand-200">jamais plus vite que sa coque la plus lente</strong>,
          gréements compris. Le jeu calcule la vitesse effective de chaque navire (sa coque, multipliée par ses{' '}
          <em>propres</em> Gréements de course), garde la <strong className="text-sand-200">plus basse</strong>, puis y
          applique la voilure de la Forge. Conséquence à connaître avant de composer un convoi&nbsp;: un seul navire non
          gréé <strong className="text-sand-200">annule le bonus de vitesse de tous les autres</strong>. Cinq gréements
          portent un légendaire de 7 à 12,3 lieues par heure&nbsp;; glisse dans le convoi un second légendaire monté en
          cales, donc sans gréement, et l'escadre entière retombe à 7. C'est l'exact inverse du Faux-fond, où le
          meilleur navire donne son taux à toute la cargaison. Chaque écran de lancement nomme, sous la durée annoncée,
          le <strong className="text-sand-200">navire qui impose l'allure</strong>&nbsp;: lis-le avant de larguer les
          amarres.
        </Note>
        <Note tone="gold" Icon={(p) => <BuildingIcon type="shipyard" {...p} />} title="Le chantier dimensionne tes raids">
          Plus ton chantier monte, plus tu peux engager de navires dans un même raid (deux par niveau de chantier).
          C'est aussi lui qui ouvre l'accès à la Frégate (niv.&nbsp;2), au Galion (niv.&nbsp;5) puis au{' '}
          <strong className="text-sand-200">Navire Légendaire</strong>, réservé au{' '}
          <strong className="text-sand-200">chantier niv.&nbsp;16</strong>. Vaisseau-amiral d'élite : une coque qui surclasse
          nettement le galion, mais qui coûte une fortune dans les trois ressources. Atteindre le niveau&nbsp;16 puis
          réunir le coût en fait un <strong className="text-sand-200">trophée de fin de saison</strong>, pas un navire de
          tous les jours ; n'en attends qu'une poignée par saison, pas une flotte.
        </Note>
        <Note tone="gold" Icon={(p) => <BuildingIcon type="capitainerie" {...p} />} title="La Capitainerie fixe tes mouillages">
          Ta flotte ne peut pas dépasser les <strong className="text-sand-200">mouillages</strong> de ta Capitainerie :{' '}
          <strong className="text-sand-200">cinq par niveau</strong>. Les épaves ne comptent pas ; les coques en
          construction, si. Au cap, <strong className="text-sand-200">seule la construction est bloquée</strong> : tes
          navires existants naviguent, réparent et raident normalement. Agrandis le port pour agrandir la flotte.
        </Note>
      </div>
      <Note tone="danger" Icon={(p) => <StatIcon {...p} name="hp" />} title="La coque garde ses cicatrices">
        Chaque combat (abordage, embuscade, raid) laisse des <strong className="text-sand-200">dégâts de coque
        persistants</strong> sur les survivants : ta flotte ne repart jamais neuve après une bataille. L'intégrité
        s'affiche en <strong className="text-sand-200">%</strong> sur chaque navire. Sous{' '}
        <strong className="text-sand-200">30&nbsp;%</strong>, un navire est{' '}
        <strong className="text-sand-200">hors d'état de naviguer</strong> : il ne peut plus embarquer sur{' '}
        <strong className="text-sand-200">aucune mission</strong> tant qu'il n'est pas réparé. Même partiellement
        abîmé, il reste sélectionnable, mais tu pars avec les PV qu'il lui reste.
      </Note>
      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="info" Icon={WoodIcon} title="Réparer au chantier : du bois, tout de suite">
          Le chantier répare <strong className="text-sand-200">instantanément</strong>, contre du{' '}
          <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" /> bois ; remettre une coque à 100&nbsp;% coûte
          environ <strong className="text-sand-200">80&nbsp;% de son bois de construction</strong> (proportionnel aux
          PV manquants : une égratignure coûte peu, une coque en lambeaux coûte cher). Toujours nettement moins cher
          que racheter le navire. Boutons <em>Réparer</em> par navire ou <em>Tout réparer</em> d'un coup.
        </Note>
        <Note tone="info" Icon={AnchorIcon} title="Le carénage : gratuit, mais lent">
          Un navire <strong className="text-sand-200">à quai</strong> (hors mission) se soigne aussi seul, sans
          dépenser un clou de bois : <strong className="text-sand-200">2&nbsp;% de ses PV max par heure</strong>, plus{' '}
          <strong className="text-sand-200">0,1&nbsp;point par niveau de chantier</strong> au-delà du premier ; un
          chantier niv.&nbsp;16 carène à 3,5&nbsp;%/h. De l'épave au complet en un à deux jours selon ton chantier :
          jamais définitivement bloqué, juste patient.
        </Note>
      </div>
      <Note tone="gold" Icon={WoodIcon} title="Construire une coque coûte plus de bois">
        Le <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" /> bois nécessaire pour mettre un navire à l'eau a
        été <strong className="text-sand-200">doublé</strong> : le capital flotte se construit (et se remplace)
        plus lentement qu'avant. La réparation en profite par ricochet (toujours moins chère que reconstruire).
      </Note>
    </Section>
  )
}

function Missions() {
  const missions: { Icon: Icon; name: string; what: ReactNode }[] = [
    { Icon: CompassIcon, name: 'Patrouille', what: <>Ta toute première sortie (≈ 45 s, sans danger). Elle rapporte un peu d'or et <strong className="text-sand-200">débloque toutes les autres expéditions</strong>.</> },
    { Icon: GoldIcon, name: 'Commerce', what: <>Charge du <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" /> bois et du <RumIcon className="inline w-3.5 h-3.5 text-sand-300" /> rhum, vends-les loin pour de l'<GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> or, ou rachète l'un ou l'autre au comptoir (et leur ressource sur place dans une scierie ou une distillerie). Tu peux y envoyer <strong className="text-sand-200">une flotte</strong> : les cales de tous les navires se cumulent. Plus la route est longue, meilleur est le prix à l'unité.</> },
    { Icon: MapIcon, name: 'Exploration', what: <>Envoie un navire cartographier une île inconnue. Tu lèves le brouillard sur la carte et tu en tires une prime, d'autant plus grosse que l'île est lointaine et exotique.</> },
    { Icon: CombatIcon, name: 'Raid', what: <>Lance une flotte sur n'importe quelle île PNJ, et c'est la <strong className="text-sand-200">nature de la cible</strong> qui décide du pavillon que tu te dessines : piller un <strong className="text-sand-200">repaire pirate</strong>, c'est servir la Couronne ; piller un <strong className="text-sand-200">comptoir</strong> ou une <strong className="text-sand-200">île de production</strong>, c'est devenir hors-la-loi ; donner l'assaut à un <strong className="text-sand-200">port de la Marine</strong>, c'est s'attaquer à la loi elle-même. Si tu l'emportes, tu repars chargé de butin. Si tu perds, des navires coulent, pour de bon.</> },
  ]
  return (
    <Section
      id="missions"
      Icon={CompassIcon}
      title="Les missions"
      lead="Tu envoies tes navires en expédition minutée. Quatre grands types, toutes « lance-et-oublie » : elles se résolvent seules à l'échéance et te notifient."
    >
      <div className="grid sm:grid-cols-2 gap-4">
        {missions.map((m) => (
          <Mini key={m.name} Icon={m.Icon} title={m.name}>{m.what}</Mini>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Note tone="info" Icon={GoldIcon} title="La part d'équipage">
          L'équipage prélève <strong className="text-sand-200">20&nbsp;% de l'or</strong> rapporté par une mission (la
          patrouille est exemptée). Le bois et le rhum, eux, ne sont jamais taxés.
        </Note>
        <Note tone="info" Icon={RumIcon} title="Les rations">
          Chaque voyage consomme du <RumIcon className="inline w-3.5 h-3.5 text-sand-300" /> rhum à l'appareillage :
          l'<strong className="text-sand-200">équipage</strong> de chaque navire multiplié par les heures de mer
          (colonne Équipage du tableau des coques). Un navire ne peut pas emporter plus de grog que sa propre cale,
          ce qui plafonne sa ration sur les très longues traversées. Une flotte affamée ne lève pas l'ancre.
        </Note>
        <Note tone="gold" Icon={StormIcon} title="Les aléas de la traversée">
          Au plus un événement par voyage : tempête, kraken, épave providentielle… Sans gravité pour l'instant
          (récit + une petite part du gain en plus ou en moins). Jamais sur une patrouille. Le kraken qu'on y
          croise n'est qu'une ombre sous la coque, pas la bête elle-même&nbsp;: celle-là se traque exprès, et
          elle a son chapitre (voir <em>Chasse légendaire</em>).
        </Note>
      </div>
      <Note tone="gold" Icon={AnchorIcon} title="Rappeler une flotte en route">
        Tant qu'une expédition n'a pas dépassé la <strong className="text-sand-200">moitié de son trajet</strong>, tu
        peux l'annuler : la flotte fait demi-tour et rentre au port. Passé ce cap elle est plus près de la cible que
        de chez toi, et le bouton disparaît (la patrouille d'accueil, elle, ne s'annule jamais). Le retour n'est{' '}
        <strong className="text-sand-200">pas instantané</strong>&nbsp;: les navires remettent à rentrer le temps
        déjà passé en mer, et ne sont utilisables qu'une fois à quai. Ce que la cale emportait redescend intact&nbsp;:
        la cargaison que tu partais vendre, ou l'<GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> or avancé
        pour un achat, puisque rien n'a été négocié. Seule la <strong className="text-sand-200">ration de</strong>{' '}
        <RumIcon className="inline w-3.5 h-3.5 text-sand-300" /> <strong className="text-sand-200">rhum</strong> est
        perdue : elle a été bue en mer. Attention à l'entrepôt, comme pour tout retour de mission : ce qui ne rentre
        plus passe par-dessus bord.
      </Note>
      <Note tone="info" Icon={CompassIcon} title="Une expédition par île à la fois">
        Tant qu'une expédition d'un <strong className="text-sand-200">type donné</strong> fait route vers une île
        (exploration, commerce, raid, contrebande, récupération de récif), tu ne peux pas en relancer une{' '}
        <strong className="text-sand-200">du même type</strong> vers cette île avant qu'elle se résolve. Des types
        différents peuvent viser la même île en parallèle.
      </Note>
    </Section>
  )
}

function Carte() {
  const types: { Icon: Icon; color: string; name: string; desc: ReactNode }[] = [
    { Icon: IslandIcon, color: '#5FBDD0', name: 'Ton repaire', desc: 'Ton île de départ : production, flotte, défenses.' },
    { Icon: PortIcon, color: '#5FBDD0', name: 'Port officiel', desc: <>Grands ports de la Marine : refuges et hauts lieux du commerce honnête. Les plus imposants sont des <strong className="text-sand-200">citadelles de guerre</strong> qu'un hors-la-loi peut prendre d'assaut, à un danger extrême.</> },
    { Icon: GoldIcon, color: '#E5CC8F', name: 'Comptoir marchand', desc: <>Hubs de négoce : les meilleurs prix de vente, et l'achat de <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" /> bois <strong className="text-sand-200">comme</strong> de <RumIcon className="inline w-3.5 h-3.5 text-sand-300" /> rhum. On peut aussi les <strong className="text-sand-200">piller</strong>, et c'est de la piraterie : commercer avec eux te pousse vers la Guilde, les piller te pousse vers le Pirate. Leur garnison n'est pas uniforme. Les quatre postes de <strong className="text-sand-200">périphérie</strong> ne gardent qu'un brick ou deux (danger 1 à 2) ; ceux du centre en alignent trois derrière un fort (danger 3).</> },
    { Icon: MineIcon, color: '#C9A55C', name: 'Île de production', desc: 'Repaire PNJ tourné vers une ressource (scierie ou distillerie) ; cible de raid de difficulté modérée.' },
    { Icon: SkullIcon, color: '#F87171', name: 'Repaire pirate', desc: <>Antres PNJ à piller. Danger 1 à 10 pour l'immense majorité, mais une poignée de <strong className="text-sand-200">forteresses apex</strong>, tout au bord de la carte, grimpent bien au-delà.</> },
    { Icon: TaleIcon, color: '#C4B5FD', name: 'Île mystérieuse', desc: <>Aux confins de la carte : <strong className="text-sand-200">site purement narratif</strong> à réputation neutre. On n'y commerce pas, on n'y fait pas de contrebande, on ne la pille pas ; tu y envoies une <strong className="text-sand-200">expédition</strong> (une flotte) et tu lis son récit à embranchements (voir <em>Les récits</em>).</> },
    { Icon: ReefIcon, color: '#5FBDD0', name: 'Récif', desc: 'Hauts-fonds jonchés d\'épaves : expédition de récupération à risque ; le seul danger est la coque (naufrage aléatoire selon le type de navire), jamais une embuscade. Une flotte de deux navires ou plus ramène toujours au moins un rescapé ; un navire parti seul n\'est protégé par rien et peut sombrer corps et biens. Butin proportionnel à la cale qui rentre (une coque coulée emporte sa part, et une expédition dont pas un navire ne revient ne rapporte donc rien du tout) : grosses coques et grande flotte rapportent davantage, et la distance bonifie le tout (un récif lointain paie plus). Chaque récif porte un stock de charges limité : une expédition en consomme une, même quand elle tourne mal, et rien ne le recharge en cours de saison (le stock ne revient qu\'au reset). Réputation neutre : aucun bonus de faction sur le butin, sa base est donc plus généreuse pour compenser ; compte comme de l\'exploration.' },
  ]
  return (
    <Section
      id="carte"
      Icon={MapIcon}
      title="La carte et le brouillard de guerre"
      lead="Le monde est une grille de 100 × 100 lieues parsemée d'une trentaine d'îles. Tu ne vois que ce que tu as découvert : le reste dort sous le brouillard."
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {types.map((t) => (
          <div key={t.name} className="flex items-start gap-3 rounded-xl border border-sand-500/15 bg-ocean-900/30 p-3.5">
            <span className="grid place-items-center w-9 h-9 shrink-0 rounded-lg bg-ocean-900/60 border border-sand-500/20" style={{ color: t.color }}>
              <t.Icon className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <p className="text-sand-200 text-sm font-medium">{t.name}</p>
              <p className="text-xs text-ocean-100 leading-snug mt-0.5">{t.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="info" Icon={MapIcon} title="Lever le brouillard">
          Tu démarres avec ton île, <strong className="text-sand-200">tous les ports de la Marine</strong>, des
          grands ports jusqu'aux <strong className="text-sand-200">forteresses apex des confins, visibles dès le
          départ</strong> (la Marine ne cache pas ses installations), et les comptoirs les plus proches déjà sur la
          carte, dont toujours <strong className="text-sand-200">au moins un poste de périphérie</strong> : une cible
          hors-la-loi est donc à ta portée dès le premier jour, sans rien explorer. Toute autre île,{' '}
          <strong className="text-sand-200">repaires pirates compris</strong>, doit être{' '}
          <strong className="text-sand-200">explorée</strong> avant d'apparaître, et avant de pouvoir être pillée.
        </Note>
        <Note tone="gold" Icon={CompassIcon} title="Ce qui fait grossir le butin">
          Trois leviers, tous cumulatifs. La <strong className="text-sand-200">durée</strong> du trajet : une cible
          lointaine paie d'autant plus, jamais moins. Le <strong className="text-sand-200">danger</strong> de la cible :
          jusqu'au danger 4, l'effet est faible (plancher anti-grief) ; au-delà, le butin monte fortement. C'est une
          prime de risque. La <strong className="text-sand-200">puissance navale</strong> de ta flotte, mesurée sur la{' '}
          <strong className="text-sand-200">moyenne</strong> de tes coques et non sur leur nombre : une flotte de
          frégates, galions ou légendaires pille davantage qu'une nuée de bricks (rendement décroissant, plafonné).
          Comme c'est une moyenne, ajouter un brick à une flotte lourde la tire vers le bas et fait baisser le bonus.
          Taille de flotte et puissance sont deux choses distinctes : la première ouvre une petite prime par navire,
          la seconde récompense la qualité des coques.
        </Note>
      </div>
      <Note tone="danger" Icon={SkullIcon} title="Aux confins : les forteresses apex des deux camps">
        Loin des côtes, chaque bord a ses <strong className="text-sand-200">forteresses apex</strong>, bien au-delà du
        danger 10 : des <strong className="text-sand-200">repaires pirates</strong> pour qui chasse au nom de la
        Couronne, des <strong className="text-sand-200">citadelles de la Marine</strong> pour qui a choisi la piraterie.
        Les plus profondes sont défendues par des <strong className="text-sand-200">Navires Légendaires</strong>. Cette
        montagne est <strong className="text-sand-200">fixe</strong> : elle ne grandit pas avec ta progression, elle
        t'attend. Tu ne la gravis pas en amassant toujours plus de navires (ta flotte reste{' '}
        <strong className="text-sand-200">plafonnée</strong> par les mouillages de ta Capitainerie, et chaque sortie
        par ton chantier), mais en montant en{' '}
        <strong className="text-sand-200">qualité</strong> : coques Légendaires, Forge et modules de combat.
      </Note>
      <Note tone="info" Icon={CombatIcon} title="La première marche, des deux bords">
        La symétrie ne vaut pas qu'aux confins, elle vaut aussi tout en bas. Chaque bord a sa{' '}
        <strong className="text-sand-200">marche d'entrée</strong> : une crique à peine gardée pour qui chasse au nom
        de la Couronne, un <strong className="text-sand-200">comptoir de périphérie</strong> pour qui commence la
        piraterie. Les deux alignent exactement la même garnison à danger égal, et l'une comme l'autre s'ouvrent à
        une <strong className="text-sand-200">paire de bricks</strong> : ni frégate, ni chantier de haut niveau. Ne
        pars pas au danger 1 avec ton seul navire de départ, en revanche : à un contre un, l'affaire ne tourne en ta
        faveur qu'une fois sur deux, et une coque coulée l'est pour de bon. Ce qui te sépare d'un pavillon n'est donc
        pas ta flotte, c'est ce que tu choisis d'attaquer.
      </Note>
      <Note tone="info" Icon={WoodIcon} title="Îles spécialisées : scieries & distilleries">
        Parmi les îles de production PNJ, certaines sont vouées à une ressource : les{' '}
        <strong className="text-sand-200">scieries</strong> au <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" />{' '}
        et les <strong className="text-sand-200">distilleries</strong> au{' '}
        <RumIcon className="inline w-3.5 h-3.5 text-sand-300" />. Tu les reconnais à leur nom sur la carte
        («&nbsp;Scierie du Cap&nbsp;», «&nbsp;Distillerie Sauvage&nbsp;»…). Les{' '}
        <strong className="text-sand-200">piller rapporte surtout leur ressource</strong>, plus un petit appoint d'
        <GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> : raider une scierie ramène un gros tas de{' '}
        <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" />, une distillerie de{' '}
        <RumIcon className="inline w-3.5 h-3.5 text-sand-300" />, un comptoir ou un port de l'
        <GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> pur, un repaire donne un mélange à dominante d'
        <GoldIcon className="inline w-3.5 h-3.5 text-sand-300" />. La part d'équipage est prélevée sur ces pièces, jamais sur ta cargaison.{' '}
        Tu peux aussi <strong className="text-sand-200">acheter leur ressource sur place</strong> (une scierie
        vend du <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" />, une distillerie du{' '}
        <RumIcon className="inline w-3.5 h-3.5 text-sand-300" />) et c'est{' '}
        <strong className="text-sand-200">la source la moins chère</strong> de leur ressource&nbsp;: moins cher
        qu'un comptoir, qui vend les deux (<WoodIcon className="inline w-3.5 h-3.5 text-sand-300" /> et{' '}
        <RumIcon className="inline w-3.5 h-3.5 text-sand-300" />) mais plus cher. En revanche, une île spécialisée{' '}
        <strong className="text-sand-200">ne rachète pas sa propre ressource</strong>&nbsp;: une scierie regorge
        déjà de <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" /> (vends-lui ton{' '}
        <RumIcon className="inline w-3.5 h-3.5 text-sand-300" />), une distillerie de{' '}
        <RumIcon className="inline w-3.5 h-3.5 text-sand-300" /> (vends-lui ton{' '}
        <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" />). Mais surtout, le{' '}
        <strong className="text-sand-200">raid d'une île spécialisée ramène un gros tas</strong> de sa
        ressource, assez pour <strong className="text-sand-200">écraser largement le{' '}
        <RumIcon className="inline w-3.5 h-3.5 text-sand-300" /> des rations</strong> brûlées sur le trajet : c'est
        le moyen le plus efficace de t'en constituer des réserves (pas d'
        <GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> à dépenser, il faut juste percer leur défense).
      </Note>
    </Section>
  )
}

function Recits() {
  return (
    <Section
      id="recits"
      Icon={ScrollQuillIcon}
      title="Les récits : le journal de tes expéditions"
      lead="Une île mystérieuse, c'est une expédition : tu y envoies une flotte. À l'accostage, son récit s'ouvre : une histoire à embranchements en plein écran. Tu lis, tu choisis, et les conséquences frappent la flotte que tu as risquée."
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <Mini Icon={AnchorIcon} title="Tu envoies une flotte">
          Depuis la carte, tu lances une expédition : choisis les navires à embarquer (coût en rhum aller-retour,
          comme une mission). La flotte voyage ; à l'arrivée le récit devient lisible. Elle reste engagée tant que
          tu ne l'as pas terminé, puis rentre au port.
        </Mini>
        <Mini Icon={BookIcon} title="Des embranchements, pas des tutoriels">
          Tu lis un passage, tu choisis une option. Les conséquences exactes ne sont jamais résumées à l'avance,
          mais les choix à tirage portent un <strong className="text-sand-200">indicateur de risque</strong>{' '}
          (1 à 3 têtes de mort : faible, modéré, élevé). À toi de juger si le jeu en vaut la chandelle : un choix
          peut rapporter des ressources, en coûter, te donner un coup de pouce en réputation, ou mal tourner
          (jusqu'à perdre un navire et, sur les paliers moyen et difficile, y laisser des ressources).
        </Mini>
        <Mini Icon={PennantIcon} title="Certains choix exigent un rang">
          Quelques options ne s'ouvrent qu'avec un rang ou un alignement précis ; elles restent visibles mais
          grisées, avec leur condition indiquée. C'est un avantage de progression, pas de paiement.
        </Mini>
        <Mini Icon={HourglassIcon} title="Rejouable : relance une expédition">
          Pas de retour arrière dans un récit en cours. Mais tu peux toujours <strong className="text-sand-200">relancer
          une expédition</strong> sur la même île : elle tire alors un autre récit de son répertoire. Plus de
          one-shot par saison.
        </Mini>
      </div>
      <Note tone="info" Icon={MapIcon} title="Difficulté affichée : savoir où aller">
        Chaque mystérieuse porte un <strong className="text-sand-200">palier fixe</strong> (très facile → difficile),
        affiché sur la carte avec une <strong className="text-sand-200">reco de flotte</strong> : tu choisis ta
        destination en connaissance de cause avant d'armer l'expédition. Les plus dures sont les plus lointaines.
      </Note>
      <Note tone="gold" Icon={SkullIcon} title="Tu ne risques que ce que tu envoies">
        Les pertes de navires frappent <strong className="text-sand-200">la flotte d'expédition</strong>,
        jamais celle restée au port. Au plus un navire par mauvais tirage, mais sur les îles les plus lointaines, une
        série de revers peut coûter toute l'expédition. Sur les paliers <strong className="text-sand-200">moyen et
        difficile</strong>, une fin d'échec peut aussi te faire perdre des{' '}
        <strong className="text-sand-200">ressources</strong> (montants modérés, jamais gonflés par les primes ; tu
        perds ce que tu as, jamais plus). À la fin, les survivants rentrent ; ce qui a coulé est perdu.
      </Note>
      <Note tone="info" Icon={MysteryIcon} title="Réputation neutre">
        Envoyer une expédition ne fait pas bouger ta réputation de faction. Seuls certains choix
        dans le récit lui-même donnent un léger nudge, et toujours de façon cohérente avec ce que tu décides.
      </Note>
      <Note tone="gold" Icon={GoldIcon} title="Ce qui grossit la récompense">
        Comme la réputation ne joue pas sur les récits, la <strong className="text-sand-200">récompense d'un choix</strong>{' '}
        bénéficie d'un <strong className="text-sand-200">plancher garanti</strong> : même une petite expédition (un seul
        navire, une île proche et facile) touche au moins ce minimum, pour que la sortie vaille toujours la traversée.
        Au-delà, le butin est amplifié par l'expédition que tu engages : la{' '}
        <strong className="text-sand-200">prime de flotte</strong> compte les navires <em>et leur qualité</em> (un
        convoi de galions paie nettement plus qu'un brick solitaire) ; la{' '}
        <strong className="text-sand-200">prime de distance</strong> grimpe avec l'éloignement de l'île ; et la{' '}
        <strong className="text-sand-200">prime de palier</strong> multiplie fort les paliers durs (le difficile paie
        plusieurs fois le facile). Un récit difficile réussi avec une flotte lourde rivalise avec les meilleurs raids.
        Les <strong className="text-sand-200">coûts et les pertes ne sont jamais gonflés</strong>, eux.
      </Note>
    </Section>
  )
}

function Combat() {
  const phases: { key: StatName; name: string; desc: ReactNode }[] = [
    { key: 'manoeuvre', name: 'Manœuvre', desc: <>Les flottes cherchent le vent. Celle qui décroche le plus de touches <em>tire la première</em> à la bordée (l'attaquant garde la main en cas d'égalité).</> },
    { key: 'bordee', name: 'Bordée', desc: <>Échange de canons à distance. Le camp qui a l'initiative tire, on applique les coups, puis l'autre riposte avec ses survivants.</> },
    { key: 'abordage', name: 'Abordage', desc: <>Corps à corps final et <em>simultané</em> : les deux camps frappent en même temps, sur les navires encore à flot.</> },
  ]
  return (
    <Section
      id="combat"
      Icon={CombatIcon}
      title="Le combat : trois phases, des dés, du blindage"
      lead={
        <>
          Toute bataille est un duel de flottes résolu aux dés (un tirage <strong className="text-sand-200">seedé</strong>,
          rejouable et auditable). Elle se joue en <strong className="text-sand-200">manches</strong> ; chaque manche
          enchaîne les trois phases ci-dessous, toujours dans le même ordre. Un même navire ne lance pas le même nombre
          de dés selon la phase : chaque coque a <strong className="text-sand-200">trois scores distincts</strong>{' '}
          (manœuvre, bordée, abordage).
        </>
      }
    >
      <div className="grid sm:grid-cols-3 gap-4">
        {phases.map((p, i) => (
          <Mini key={p.key} Icon={(props) => <StatIcon {...props} name={p.key} />} title={`${i + 1}. ${p.name}`}>
            {p.desc}
          </Mini>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="info" Icon={(p) => <StatIcon {...p} name="manoeuvre" />} title="Toucher : 5 ou 6">
          Chaque navire lance une poignée de dés à six faces ; un <strong className="text-sand-200">5 ou 6</strong> est un
          coup au but. On ne relève jamais cette barre ; la puissance se gagne via la Forge (relances de dés), pas
          en truquant le seuil.
        </Note>
        <Note tone="info" Icon={(p) => <StatIcon {...p} name="armure" />} title="Armure : le seuil de perce">
          L'<strong className="text-sand-200">armure</strong> n'est pas un stock de PV : c'est un seuil de perce. Pour
          qu'un coup entame une cible, le score de la phase qui tire doit <strong className="text-sand-200">atteindre</strong> son
          armure : l'<strong className="text-sand-200">égaler suffit</strong> (≥), inutile de la dépasser ; sinon le boulet
          ricoche et le coup est perdu. La <strong className="text-sand-200">coque</strong>, elle, est le nombre de coups
          encaissés avant de sombrer.
        </Note>
      </div>
      <Note tone="info" Icon={CombatIcon} title="La brèche : un gros navire ouvre la voie">
        Le score comparé à l'armure, c'est celui de ta <strong className="text-sand-200">coque la plus puissante</strong> dans
        la phase, <strong className="text-sand-200">pas la somme</strong> de la flotte. Si ton meilleur navire atteint
        l'armure adverse, <strong className="text-sand-200">toute la volée passe</strong> : tes navires légers s'engouffrent
        dans la brèche et touchent eux aussi. Mais si <strong className="text-sand-200">aucune</strong> de tes coques
        n'atteint cette armure, tout ricoche. Un brick seul ne raiera donc jamais un galion (cent bricks non plus),
        alors qu'un brick escorté d'une frégate ou d'un galion perce dans son sillage. Pour briser un mur d'armure,
        amène au moins une coque assez lourde : elle entraîne le reste.
      </Note>
      <Note tone="danger" Icon={CombatIcon} title="Gagner ≠ rentrer indemne">
        Une victoire signifie seulement que tu as coulé toute la flotte adverse ; tu peux l'emporter et perdre tout
        de même un ou deux navires. Tes <strong className="text-sand-200">coques les moins blindées sont visées en
        premier</strong> : un petit navire bon marché sert de bouclier aux gros. Un navire qui survit rentre intact,
        mais <strong className="text-sand-200">un navire coulé est perdu définitivement</strong>. Et si nul ne cède au
        bout de dix manches, le défenseur tient : l'assaillant rentre bredouille.
      </Note>
    </Section>
  )
}

/**
 * Chasse légendaire (fiche 32) — le boss de fin de saison.
 *
 * Ne décrire QUE le livré. Le design a été refondu le 2026-07-30 : la bête est UN
 * combattant (KrakenFleet ne rend qu'un FleetShip), elle se REGÉNÈRE entièrement
 * entre deux tentatives (aucun paramètre de PV restants sur le builder), le
 * capitaine n'a que 4 tentatives (KrakenArchetype::maxDives + 1) et un échec
 * ANÉANTIT la flotte engagée (MissionCompletionService::resolveKrakenHunt).
 *
 * Aucun pourcentage de chance de victoire côté serveur (HuntThreat n'a pas de
 * winRate) : ne jamais en promettre un ici, et ne pas non plus en fabriquer un
 * déguisé en « une fois sur deux ». Les verdicts par taille de flotte sont
 * qualitatifs, c'est voulu.
 */
function Kraken() {
  // Mesuré contre le moteur de combat (200 batailles par ligne, graines figées,
  // Forge poussée). Le Chantier vient de la règle de cap déjà documentée plus
  // haut : deux coques engagées par niveau.
  const runs: { fleet: string; note: string; shipyard: string; verdict: string }[] = [
    { fleet: '32 coques scindées', note: 'le plafond du gros du peloton', shipyard: '16', verdict: 'elle tient, à chaque fois' },
    { fleet: '38 coques scindées', note: 'plus près, pas assez près', shipyard: '19', verdict: 'elle tient encore' },
    { fleet: '40 coques scindées', note: 'la coupe de référence', shipyard: '20', verdict: 'un vrai pari' },
    { fleet: '42 coques et plus', note: 'la marge, pas un raccourci', shipyard: '21', verdict: 'elle tombe' },
    { fleet: 'gréement unique, à toute taille', note: 'une seule phase mord', shipyard: 'peu importe', verdict: 'elle ne tombe jamais' },
  ]
  return (
    <Section
      id="kraken"
      Icon={StormIcon}
      title="La Chasse légendaire : faire remonter la bête"
      lead={
        <>
          Tout au bout de la saison, il reste une cible qui <strong className="text-sand-200">n'est pas une
          île</strong>. Quelque chose de très vieux dort dans l'abîme&nbsp;; on ne le trouve pas, on le{' '}
          <strong className="text-sand-200">fait remonter</strong>. La bête paraît alors en haute mer, à un point
          que personne n'a choisi. Il n'y a rien à piller là-bas&nbsp;: on y va pour l'abattre, et pour pouvoir
          dire qu'on y était.
        </>
      }
    >
      <Note tone="danger" Icon={SkullIcon} title="À lire avant tout le reste : c'est tout ou rien">
        Une flotte qui n'abat pas la bête <strong className="text-sand-200">ne rentre pas</strong>. Pas
        «&nbsp;quelques pertes&nbsp;»&nbsp;: la flotte entière est engloutie, y compris les coques encore à flot
        à la fin du combat, et chacune est <strong className="text-sand-200">perdue définitivement</strong>. Tu
        n'engages pas une escadre sur cette cible, tu la <strong className="text-sand-200">mises</strong>. Les
        plafonds anti-grief qui protègent ton dock ne jouent pas ici&nbsp;: ils bordent les assauts que tu{' '}
        <em>subis</em>, jamais une sortie que tu décides.
      </Note>
      <div className="grid sm:grid-cols-3 gap-4">
        <Mini Icon={TreasureMapIcon} title="Un rite en trois temps">
          On ne la croise jamais par hasard. Le rite s'ouvre de lui-même dès qu'une coque{' '}
          <strong className="text-sand-200">Légendaire</strong> bat pavillon dans ton port, et c'est une{' '}
          <strong className="text-sand-200">quête en chaîne</strong> qui n'a pas d'échéance&nbsp;: prends le temps
          qu'il te faut, elle ne s'effacera pas du tableau.
        </Mini>
        <Mini Icon={MapIcon} title="Un point en pleine mer">
          Ni repaire, ni comptoir&nbsp;: pas de brouillard à lever, rien à explorer, rien à négocier. Un seul
          ordre existe, lever l'ancre et l'attaquer. Ta flotte fait route, et l'assaut se résout tout seul à
          l'échéance, comme n'importe quelle mission.
        </Mini>
        <Mini Icon={CombatIcon} title="Un assaut, dix manches">
          Chaque tentative est une bataille ordinaire, tirée aux dés seedés et bornée à{' '}
          <strong className="text-sand-200">dix manches</strong> (voir <em>Le combat</em>). Au bout des dix
          manches, le défenseur tient&nbsp;: il faut donc l'abattre dans ce compte, ou pas du tout.
        </Mini>
      </div>
      <Note tone="info" Icon={TreasureMapIcon} title="Les trois gestes du rite">
        <p>
          Trois étapes, dans cet ordre, chacune n'ouvrant la suivante qu'une fois accomplie&nbsp;:
        </p>
        <ol className="mt-2 space-y-1.5 list-decimal list-inside">
          <li>
            <strong className="text-sand-200">Sonder l'abîme</strong>&nbsp;: remporter un pillage sur une cible de{' '}
            <strong className="text-sand-200">danger 21 ou plus</strong>, où que ce soit sur la carte.
          </li>
          <li>
            <strong className="text-sand-200">L'offrande</strong>&nbsp;: convoyer{' '}
            <RumIcon className="inline w-3.5 h-3.5 text-sand-300" /> jusqu'au{' '}
            <strong className="text-sand-200">Récif du Kraken</strong>, en contrebande, puisqu'un repaire n'achète
            rien de légal. Il faut en écouler une bonne cale, pas une gorgée.
          </li>
          <li>
            <strong className="text-sand-200">Le réveil</strong>&nbsp;: piller le Récif lui-même, et l'emporter.
          </li>
        </ol>
        <p className="mt-2">
          La dernière étape franchie, tu réclames le rite et la mer se creuse&nbsp;: la bête paraît en haute mer.
          Une par saison, et une seule.
        </p>
      </Note>
      <Note tone="gold" Icon={HourglassIcon} title="Elle repart intacte, et tu n'as que quatre essais">
        Entre deux tentatives, la bête <strong className="text-sand-200">referme toutes ses plaies</strong>.
        Rien ne s'accumule d'un assaut au suivant&nbsp;: ce que tu lui as arraché est un score, pas un progrès,
        et la tentative d'après la retrouve entière. Tu disposes de{' '}
        <strong className="text-sand-200">quatre tentatives</strong> pour la saison, pas une cinquième&nbsp;:
        après la quatrième elle disparaît dans l'abîme jusqu'au prochain monde. Autant dire les choses
        franchement&nbsp;: entre deux essais, il te faudra <strong className="text-sand-200">reconstruire une
        flotte entière</strong>, puisque le premier est resté au fond.
      </Note>
      <Note tone="danger" Icon={(p) => <StatIcon {...p} name="armure" />} title="Le mur, c'est l'armure">
        Elle ne t'oppose pas d'abord des points de coque, mais un <strong className="text-sand-200">seuil de
        perce</strong>&nbsp;: son armure vaut <strong className="text-sand-200">17</strong>. Un Galion, même tout
        en canons, plafonne à 13&nbsp;: il ricoche. Une coque{' '}
        <strong className="text-sand-200">Légendaire nue</strong> plafonne à 16&nbsp;: elle ricoche aussi. Les
        modules de combat ne sont donc plus un bonus, ils sont la condition d'entrée, et leurs recettes se
        débloquent à la Forge. Et comme la brèche est collective mais se compte{' '}
        <strong className="text-sand-200">phase par phase</strong> (voir <em>Le combat</em>), aucune coque ne
        perce les deux phases à elle seule&nbsp;: il faut une Légendaire{' '}
        <strong className="text-sand-200">tout-canons</strong> (bordée 21) <strong className="text-sand-200">et</strong>{' '}
        une <strong className="text-sand-200">tout-grappins</strong> (abordage 17) dans la même flotte. Une
        flotte gréée à l'identique d'un bout à l'autre ne mord que d'un côté, et ça ne coûte pas seulement du
        temps&nbsp;: à ce régime, elle <strong className="text-sand-200">ne l'abat jamais</strong>, quel qu'en
        soit le nombre. Seule une flotte nombreuse peut se payer le luxe de scinder ses coques en spécialistes.
      </Note>
      <Note tone="info" Icon={MysteryIcon} title="Elle ne faiblit pas en combattant">
        Une île qu'on assiège s'épuise&nbsp;: chaque coque coulée retire ses dés de la volée suivante, et la
        défense s'effondre à mesure que la bataille avance. La bête, elle, est{' '}
        <strong className="text-sand-200">un seul adversaire</strong>&nbsp;: ses bras, ses gueules, ses voiles
        fantômes ne sont pas des cibles séparées, tout est dans le même corps. Elle frappe donc{' '}
        <strong className="text-sand-200">aussi fort à la dixième manche qu'à la première</strong>, et il n'y a
        rien à écrémer avant elle pour souffler. C'est de là que vient sa difficulté, pas de son tas de points
        de coque.
      </Note>
      <Note tone="info" Icon={TargetIcon} title="Elle plonge, et il faut la rejoindre">
        Si elle survit à tes dix manches, la bête <strong className="text-sand-200">replonge</strong> et refait
        surface ailleurs en haute mer, toujours loin de là où tu l'as perdue. Elle se dérobe presque toujours la
        première fois, plus rarement ensuite, et <strong className="text-sand-200">jamais plus de trois
        fois</strong>&nbsp;: après quoi elle reste <strong className="text-sand-200">acculée</strong> là où tu
        l'as laissée. Sa fuite ne la soigne pas et ne te punit pas&nbsp;: elle ne coûte que de la mer, le temps
        d'un nouveau trajet. Une seule chasse en vol à la fois.
      </Note>
      <Note tone="gold" Icon={(p) => <BuildingIcon type="shipyard" {...p} />} title="Ce que ça demande vraiment">
        <p>
          Le nombre de coques que tu peux engager d'un coup reste celui de ton{' '}
          <strong className="text-sand-200">Chantier naval</strong> (deux par niveau). Voici ce que ça donne
          contre elle, mesuré assaut par assaut&nbsp;:
        </p>
        <div className="mt-3 rounded-xl border border-sand-500/20 bg-ocean-900/30 p-2 overflow-x-auto">
          <table className="w-full min-w-[30rem] text-sm">
            <thead>
              <tr className="text-ocean-300">
                <th className="text-left font-normal px-3 py-2">Flotte engagée</th>
                <th className="px-2 py-2 font-normal">
                  <span className="inline-flex flex-col items-center gap-1">
                    <BuildingIcon type="shipyard" className="w-4 h-4 text-sand-300" />
                    <span className="text-[11px]">Chantier</span>
                  </span>
                </th>
                <th className="text-left px-3 py-2 font-normal">
                  <span className="inline-flex items-center gap-1.5">
                    <CombatIcon className="w-4 h-4 text-sand-300" />
                    <span className="text-[11px]">En un assaut</span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sand-500/10">
              {runs.map((r) => (
                <tr key={r.fleet}>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-2 text-sand-200 font-medium">
                      <ShipIcon type="legendary" className="w-5 h-5 text-sand-300" />
                      {r.fleet}
                    </span>
                    <span className="block text-[11px] text-ocean-300 mt-0.5">{r.note}</span>
                  </td>
                  <td className="px-2 py-2.5 text-center tabular-nums text-ocean-100">{r.shipyard}</td>
                  <td className="px-3 py-2.5 text-ocean-100">{r.verdict}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3">
          Autrement dit&nbsp;: quarante coques légendaires dans une même sortie supposent un{' '}
          <strong className="text-sand-200">Chantier niveau 20</strong>, soit quatre niveaux au-dessus de celui
          qui débloque la Légendaire, et un niveau de plus pour cesser de parier (
          <strong className="text-sand-200">Chantier 21</strong>, donc un{' '}
          <strong className="text-sand-200">Quartier-Général 19</strong> pour seulement y avoir droit). C'est la
          première raison, dans tout le jeu, de pousser ces deux bâtiments si haut. Et la marche est
          raide&nbsp;: deux coques de moins et elle tient, deux de plus et elle tombe. Le pari se joue là, sur
          une poignée de coques.
        </p>
      </Note>
      <Note tone="danger" Icon={WoodIcon} title="Aucun butin, et la facture">
        La bête ne lâche pas une pièce&nbsp;: ni <GoldIcon className="inline w-3.5 h-3.5 text-sand-300" />, ni{' '}
        <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" />, ni{' '}
        <RumIcon className="inline w-3.5 h-3.5 text-sand-300" />. <strong className="text-sand-200">Aucun
        butin</strong>, aucun module, aucune coque en récompense&nbsp;: rien qui se dépense, rien qui rende ta
        flotte plus forte. En face, même une victoire se paie&nbsp;: compte{' '}
        <strong className="text-sand-200">huit à neuf coques coulées</strong> quand elle tombe, et la flotte
        entière quand elle tient. Pour chaque coque à remettre sur cale{' '}
        <ResourceTally gold={20000} wood={20000} rhum={4000} className="text-sand-200" />, et à ces niveaux ce
        n'est pas ta caisse qui te freinera, ce sont tes scieries. Une traque, c'est une dépense de fin de règne.
      </Note>
      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="gold" Icon={PrestigeIcon} title="Ce qu'on en rapporte : un titre, et du renom">
          L'abattre donne un <strong className="text-sand-200">titre</strong> propre à elle («&nbsp;Tueur de
          Kraken&nbsp;», «&nbsp;Briseur d'armada&nbsp;») qu'aucune autre voie du jeu ne distribue, et{' '}
          <PrestigeReward n={25} /> de prestige. Le titre s'équipe dans <em>la cabine du capitaine</em>&nbsp;; il
          appartient à ton compte et <strong className="text-sand-200">survit aux saisons</strong>, comme le
          prestige. La chasse, elle, est saisonnière.
        </Note>
        <Note tone="info" Icon={MapIcon} title="On ne t'annonce aucun pronostic">
          Sur une chasse, <strong className="text-sand-200">aucun pourcentage</strong> ne t'est affiché, nulle
          part, et ce n'est pas un oubli. Une île se renseigne, une bête se juge&nbsp;: on te donne ses dés, son
          armure, ses points de coque, l'avertissement de{' '}
          <strong className="text-sand-200">ricochet</strong> si tes coques ne peuvent pas l'entamer, et ce que
          coûterait de reconstruire la flotte que tu t'apprêtes à miser. Le reste est ton estimation, et aucun
          éclaireur ne viendra t'en dire davantage.
        </Note>
      </div>
      <Note tone="info" Icon={StormIcon} title="Rien à voir avec le kraken des traversées">
        Le <strong className="text-sand-200">kraken</strong> qui secoue parfois un convoi (voir{' '}
        <em>Les missions</em>) n'est qu'un aléa de voyage&nbsp;: une frayeur de passage, un récit et une petite
        part du gain en plus ou en moins. Ici, c'est la <strong className="text-sand-200">bête elle-même</strong>,
        et elle ne passe pas&nbsp;: elle attend qu'on vienne.
      </Note>
      <Note tone="info" Icon={MysteryIcon} title="Deux bêtes se relaient, dans un ordre fixé d'avance">
        Deux créatures tiennent le rôle&nbsp;: <strong className="text-sand-200">Le Kraken</strong>, qui préfère
        t'aborder et t'écraser au corps à corps, et <strong className="text-sand-200">L'Armada fantôme</strong>,
        qui te met en pièces à la bordée. Même armure, même endurance, même compte de tentatives&nbsp;; seul le
        profil de ses dés change, donc la façon dont tu gréeras tes coques pour l'encaisser. Laquelle vient ne se
        tire pas au sort&nbsp;: c'est une <strong className="text-sand-200">rotation</strong>, réglée sur le
        numéro du monde. Le Kraken tient les <strong className="text-sand-200">deux premières saisons</strong>,
        l'Armada fantôme prend la suite&nbsp;: armer une flotte capable de seulement l'approcher demande plus
        d'une saison, et il fallait bien laisser au Kraken une seconde occasion de tomber.
      </Note>
      <Note tone="gold" Icon={CrownIcon} title="Une saison peut se terminer sans qu'elle tombe">
        Rien ne garantit qu'elle sera abattue. Quatre tentatives, et si aucune n'aboutit, elle replonge pour de
        bon&nbsp;: pas de titre, pas de prestige, pas de consolation, et le rideau tombe quand même. C'est le
        sel de la chose, un adversaire qu'on ne peut pas rater n'en est pas un. Sois prévenu&nbsp;: ce contenu
        s'adresse à <strong className="text-sand-200">deux ou trois capitaines</strong> par saison, ceux qui ont
        tout poussé au bout (le Chantier, la Forge, et une flotte de coques légendaires équipées). Si tu
        débutes, note-le pour plus tard et retourne bâtir.
      </Note>
    </Section>
  )
}

function Embuscades() {
  return (
    <Section
      id="embuscades"
      Icon={SkullIcon}
      title="Embuscades en mer : la mer a des dents"
      lead={
        <>
          Le grand large n'est plus un simple couloir de transit. Une mission qui transporte de la valeur (
          <strong className="text-sand-200">commerce</strong> ou <strong className="text-sand-200">contrebande</strong>
          ) peut être prise en chasse au retour : une vraie{' '}
          <strong className="text-sand-200">bataille navale seedée</strong>, pas un simple tirage à pile ou face. Ta
          flotte de mission défend sa cargaison contre l'assaillant. Le <strong className="text-sand-200">récif</strong>{' '}
          n'est pas concerné : son seul risque est la coque (voir chapitre <em>La carte</em>), jamais une embuscade.
        </>
      }
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <Mini Icon={SkullIcon} title="Qui t'attaque, selon ta cargaison">
          Un marchand dans la <strong className="text-sand-200">légalité</strong> (commerce) se fait tomber
          dessus par des <span className="whitespace-nowrap">🏴‍☠️ <strong className="text-sand-200">pirates</strong></span>
          . Un <strong className="text-sand-200">contrebandier</strong> se fait intercepter par une{' '}
          <span className="whitespace-nowrap">⚓ <strong className="text-sand-200">patrouille de la Marine</strong></span>,
          la même menace qu'avant, mais qui se règle maintenant aux dés plutôt qu'au hasard pur.
        </Mini>
        <Mini Icon={(p) => <StatIcon {...p} name="hp" />} title="Tu pars avec les PV que tu as">
          Une flotte déjà abîmée combat avec ses <strong className="text-sand-200">PV courants</strong> : partir en
          mission sans avoir réparé se paie ici. (Voir le chapitre <em>La flotte</em> pour la coque persistante.)
        </Mini>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="danger" Icon={SkullIcon} title="La défaite : cargaison perdue">
          Si ta flotte cède, la <strong className="text-sand-200">cargaison est perdue</strong> (confisquée pour la
          contrebande, envolée pour le commerce) et les navires touchés encaissent des dégâts de coque.
          Anti-grief : au plus <strong className="text-sand-200">un seul navire coulé</strong> par embuscade, jamais
          plus ; les autres rentrent en piteux état mais rentrent.
        </Note>
        <Note tone="gold" Icon={GoldIcon} title="La victoire : tu gardes tout, et plus encore">
          Si tu repousses l'assaut, ta <strong className="text-sand-200">cargaison reste intacte</strong> et tu
          empoches une petite <strong className="text-sand-200">prime d'épaves</strong> en{' '}
          <GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> or (d'autant plus généreuse que la mer était
          dangereuse). Un marchand qui repousse des pirates gagne aussi un peu de{' '}
          <strong className="text-sand-200">loyauté</strong>, comme repousser un raid sur son île.
        </Note>
      </div>
      <Note tone="info" Icon={CombatIcon} title="Le contrebandier peut forcer le blocus">
        Face à la Marine, tu n'es plus condamné à la confiscation automatique : si ton{' '}
        <strong className="text-sand-200">escorte</strong> gagne la bataille, ta flotte{' '}
        <strong className="text-sand-200">force le blocus</strong> ; la marchandise est vendue ou livrée comme
        prévu, malgré l'interception. Ce n'est qu'en cas de défaite que la Marine saisit la cargaison.
      </Note>
      <Note tone="info" Icon={PennantIcon} title="Le commerce légal : rare, et ça ne vise que le gros coup">
        Sur une mission de <strong className="text-sand-200">commerce</strong>, le risque ne dépend plus du danger de
        la destination : il grandit avec la <strong className="text-sand-200">distance parcourue</strong> et le{' '}
        <strong className="text-sand-200">remplissage de la cale</strong>, les deux jauges de la récompense. Un
        aller-retour court ou peu chargé navigue <strong className="text-sand-200">quasiment tranquille</strong> ;
        seul un long-courrier à pleine cale attire vraiment les pirates, jusqu'à un plafond bas, jamais l'assurance
        de tomber dans une embuscade. La cale vide n'attire aucune convoitise.
      </Note>
      <Note tone="danger" Icon={SkullIcon} title="La menace se taille sur ta cargaison, pas sur la destination">
        Quand l'assaut tombe, sa <strong className="text-sand-200">force</strong> (la meute de pirates en commerce
        comme le <strong className="text-sand-200">blocus de la Marine</strong> en contrebande) n'est plus fixée par
        la destination : elle grandit avec la <strong className="text-sand-200">valeur en or du butin transporté</strong>.
        Un petit chargement n'attire qu'une poignée de Bricks ; un très gros coup (cale pleine, navires modernisés)
        fait venir une flottille menée par des Galions. Côté contrebande, c'est le nerf du risque : plus ta cargaison
        vaut cher au noir, plus le blocus est lourd, au point qu'un gros coup exige d'échanger de la cale contre une
        escorte. L'estimation d'un trajet (commerce comme contrebande) affiche la{' '}
        <strong className="text-sand-200">composition probable</strong> de cette flotte et un indicateur de{' '}
        <strong className="text-sand-200">menace</strong> (faible/modérée/forte) qui compare ta flotte engagée à
        elle : une aide à la décision, pas une garantie sur l'issue des dés.
      </Note>
      <Note tone="info" Icon={PennantIcon} title="Aucune embuscade sous le bouclier des nouveaux capitaines">
        Quelle que soit la mission, aucune embuscade ne se déclenche sous le{' '}
        <strong className="text-sand-200">bouclier des nouveaux capitaines</strong>.
      </Note>
      <Note tone="gold" Icon={AnchorIcon} title="L'escorte devient un vrai choix">
        Rien n'empêche d'embarquer des <strong className="text-sand-200">navires de combat</strong> dans un convoi
        (de <strong className="text-sand-200">commerce</strong> comme de <strong className="text-sand-200">contrebande</strong>) :
        ils ne portent pas de cargaison, mais ils tiennent la ligne si l'assaut tombe. Composer sa flotte, c'est
        désormais aussi choisir entre <strong className="text-sand-200">plus de cale</strong> ou{' '}
        <strong className="text-sand-200">plus de coques armées</strong>.
      </Note>
      <Note tone="info" Icon={(p) => <StatIcon {...p} name="abordage" />} title="Revoir la bataille">
        Le rapport de mission garde la trace de l'embuscade ou de l'interception, et un bouton{' '}
        <em>« Revoir la bataille »</em> rejoue le combat dé par dé, comme pour un raid.
      </Note>
    </Section>
  )
}

function Defense() {
  const structures: { type: string; name: string; role: string }[] = [
    { type: 'fort', name: 'Fort', role: 'Le donjon : armure et coque épaisses, il encaisse le siège.' },
    { type: 'battery', name: 'Batterie côtière', role: 'Canons lourds qui tonnent à distance dès la bordée.' },
    { type: 'militia', name: 'Milice', role: 'Repousse les assaillants à l\'abordage.' },
    { type: 'watchtower', name: 'Tour de guet', role: 'La vigie : tire la première, dévoile le prochain raid (plus elle est haute, plus elle en révèle), et sa garnison drillée retente ses tirs ratés (bonus de relance à la bordée et à l\'abordage).' },
  ]
  // Poids de chaque rang dans le tarif du branle-bas, en part du tarif d'un
  // légendaire (miroir de App\Domain\Defense\MobilizationUpkeep::hullWeight, qui
  // le dérive du ratio de puissance de combat). Le tarif lui-même n'est PAS un
  // forfait : il dépend de la production et de la réserve de l'île, donc aucun
  // nombre en rhum ou en or ne peut être affiché ici.
  const upkeep: { type: string; name: string; share: string }[] = [
    { type: 'brick', name: 'Brick', share: '20 %' },
    { type: 'fregate', name: 'Frégate', share: '34 %' },
    { type: 'galion', name: 'Galion', share: '52 %' },
    { type: 'legendary', name: 'Navire Légendaire', share: '100 %' },
  ]
  return (
    <Section
      id="defense"
      Icon={DefenseShield}
      title="Défendre l'île contre les assauts"
      lead="Des flottes PNJ viennent piller les repaires de renom. Tu t'en protèges en élevant quatre types de structures de défense. Et plus ta réputation est profonde, plus l'ennemi frappe fort."
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {structures.map((s) => (
          <Mini key={s.type} Icon={(p) => <DefenseIcon type={s.type} {...p} />} title={s.name}>
            {s.role}
          </Mini>
        ))}
      </div>
      <Note tone="gold" Icon={(p) => <DefenseIcon type="fort" {...p} />} title="Le Fort encaisse le premier, les autres tirent derrière">
        <p>
          L'ordre dans lequel ta défense reçoit les coups n'est pas laissé au hasard. Le{' '}
          <strong className="text-sand-200">Fort passe devant</strong>&nbsp;: c'est la pièce bâtie pour absorber, la plus
          épaisse et la moins bavarde en artillerie. Viennent ensuite tes{' '}
          <strong className="text-sand-200">postes de tir</strong> (Batterie, Milice, Tour de guet), et enfin, si tu as
          sonné le branle-bas, tes <strong className="text-sand-200">coques mobilisées</strong>. Les pierres encaissent
          devant, les navires tirent derrière.
        </p>
        <p className="mt-2">
          Pourquoi ça compte&nbsp;: ta Batterie est le plus gros canon de l'île, mais elle est mince. Si elle prenait la
          première bordée, elle sombrerait <strong className="text-sand-200">sans avoir tiré</strong>. Le Fort est là
          pour lui acheter le temps de parler. Monter ton Fort, c'est donc allonger la vie de tout le reste.
        </p>
        <p className="mt-2">
          Un assaillant <strong className="text-sand-200">trop léger pour entamer ton Fort</strong> ne voit pas ses
          boulets se perdre pour autant&nbsp;: il tire sur ce qui se trouve derrière. Le Fort est un bouclier, jamais une
          muraille magique qui rendrait l'île invulnérable aux petites coques.
        </p>
      </Note>
      <Note tone="gold" Icon={(p) => <StatIcon {...p} name="hp" />} title="Monter une structure achète surtout de la survie">
        <p>
          Un niveau de plus ajoute des dés, mais il ajoute <strong className="text-sand-200">bien plus de coque</strong>,
          et c'est voulu. Une pièce d'artillerie qui coule avant d'avoir tiré ne sert à rien&nbsp;: dans un assaut,
          l'ennemi ouvre par une bordée massive, et ce qui n'y survit pas ne tirera jamais. La solidité de tes murs
          décide donc du <strong className="text-sand-200">nombre de volées que ta défense a le droit de tirer</strong>,
          ce qui compte davantage que la puissance de chaque volée.
        </p>
        <p className="mt-2">
          Conséquence pratique&nbsp;: contre une armada très supérieure, tes murs ne te feront pas gagner, mais ils
          rendront la victoire <strong className="text-sand-200">ruineuse pour l'assaillant</strong>. Un capitaine qui
          enfonce une île bien fortifiée y laisse une bonne part de sa flotte, pour un butin qui reste plafonné à un
          quart de tes réserves. C'est la vraie dissuasion&nbsp;: rendre le raid mauvais en affaires, même gagné.
        </p>
      </Note>
      <Note tone="info" Icon={PennantIcon} title="Ta notoriété est une cible, ta forteresse aussi">
        Tu n'es pas raidé au hasard&nbsp;: DEUX choses désignent ton repaire. D'abord ta{' '}
        <strong className="text-sand-200">réputation</strong>. Plus tu t'<strong className="text-sand-200">affirmes</strong>{' '}
        (peu importe le quadrant, que tu sois Marchand ou Guerrier, Loyal ou Hors-la-loi), plus les flottes lancées contre
        toi partent <strong className="text-sand-200">fortes</strong>&nbsp;: une légende fait venir des armadas, un nom anodin
        n'attire que des poignées de gueux. Ensuite ta propre{' '}
        <strong className="text-sand-200">puissance défensive</strong>&nbsp;: une île lourdement fortifiée attise les
        convoitises même à pavillon discret ; ni tes bâtiments de production ni ton or ne te désignent, mais tes{' '}
        <strong className="text-sand-200">structures de défense</strong>, oui. La force de l'assaut suit le{' '}
        <strong className="text-sand-200">plus élevé</strong> de ces deux planchers&nbsp;; la suite dépend aussi de ce que
        tu sais <strong className="text-sand-200">repousser</strong> (voir plus bas).
      </Note>
      <Note tone="info" Icon={DefenseShield} title="Profil bas = peu de raids">
        Rester <strong className="text-sand-200">neutre et discret</strong>, c'est qu'on te laisse globalement
        tranquille&nbsp;: au centre, on n'envoie pas d'armada contre un inconnu, et les assauts restent{' '}
        <strong className="text-sand-200">rares</strong>. <strong className="text-sand-200">Faire profil bas est une
        stratégie valide</strong> : une petite île sans renom reste une petite proie, tant que ses défenses restent
        modestes. Mais attention&nbsp;: si tu te <strong className="text-sand-200">fortifies lourdement</strong>, la force
        de l'assaut grimpe <strong className="text-sand-200">dès le départ</strong>, même à pavillon discret ; une
        forteresse imposante ne passe jamais inaperçue, et elle grimpe encore à chaque assaut que tu repousses. La gloire,
        elle, garde son prix, et c'est un prix double&nbsp;: chaque rang gravi rend tes ennemis plus hardis d'emblée, et
        il accélère aussi la <strong className="text-sand-200">vitesse</strong> à laquelle ils montent en puissance
        contre toi. À murs égaux, un inconnu est chassé lentement et une Légende est chassée vite.
      </Note>
      <Note tone="gold" Icon={(p) => <DefenseIcon type="fort" {...p} />} title="Des défenses sans plafond">
        Tes structures montent <strong className="text-sand-200">aussi haut que ton Quartier-général</strong> ; il n'y a
        pas de niveau maximum&nbsp;: tant que tu élèves ton QG, tu peux renforcer tes défenses. Le coût croît{' '}
        <strong className="text-sand-200">doucement</strong> (au rythme de ta production), payé surtout en{' '}
        <GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> or et <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" /> bois,
        avec une part significative de <RumIcon className="inline w-3.5 h-3.5 text-sand-300" /> rhum&nbsp;: réinvestir une fraction de
        tes revenus suffit à rester défendable, même à réputation profonde.
      </Note>
      <Note tone="info" Icon={(p) => <StatIcon {...p} name="armure" />} title="L'armure de tes défenses se renforce par paliers">
        Le <strong className="text-sand-200">Fort</strong> reste ton seul mur lourdement blindé, mais la{' '}
        <strong className="text-sand-200">Milice</strong> et la <strong className="text-sand-200">Batterie</strong> durcissent
        elles aussi leur <strong className="text-sand-200">armure</strong> en montant en niveau&nbsp;: elle passe de 1 à 2 vers le{' '}
        niveau&nbsp;5, puis à 3 vers le niveau&nbsp;9 (plafonnée là). Conséquence&nbsp;: au mitan de partie, un{' '}
        <strong className="text-sand-200">brick seul cesse de percer</strong> ces structures ; il faut une{' '}
        <strong className="text-sand-200">frégate ou un galion</strong> dans la flotte pour ouvrir la brèche (l'armure reste
        toujours sous leur seuil de perce, jamais imprenable). Le bouton{' '}
        <em>«&nbsp;Voir l'évolution par niveau&nbsp;»</em> sous chaque structure détaille armure, coque, dés et coûts à venir.
      </Note>
      <Note tone="info" Icon={(p) => <DefenseIcon type="fort" {...p} />} title="Répit après l'acharnement, premier contact anodin">
        Le rythme des assauts n'est pas implacable. Ta toute <strong className="text-sand-200">première attaque est
        triviale</strong>, le temps d'apprendre à tenir un siège. Et si tu encaisses{' '}
        <strong className="text-sand-200">plusieurs brèches d'affilée</strong>, l'ennemi te laisse{' '}
        <strong className="text-sand-200">souffler</strong>&nbsp;: les attaques s'espacent, le temps de relever tes
        défenses. Une brèche ne fait pas que suspendre l'escalade, elle la{' '}
        <strong className="text-sand-200">fait reculer d'un cinquième</strong> (et d'au moins deux crans, pour que le
        répit compte aussi sur les petites forces)&nbsp;: l'assaut suivant sera nettement plus doux que celui qui vient
        de passer, et tu disposes d'au moins deux assauts pour relever tes murs avant de retrouver ce niveau, bien
        davantage si ton pavillon reste discret. Se faire piller n'aggrave donc jamais le danger, ça le soulage.
        <span className="block mt-2">
          À l'inverse, <strong className="text-sand-200">chaque assaut que tu repousses hausse la force du
          suivant</strong>, et cette hausse est <strong className="text-sand-200">d'autant plus vive que ton pavillon est
          connu</strong>&nbsp;: elle se compte en part de l'assaut que tu viens d'encaisser, de{' '}
          <strong className="text-sand-200">4&nbsp;%</strong> pour un capitaine anonyme à{' '}
          <strong className="text-sand-200">15&nbsp;%</strong> pour une <strong className="text-sand-200">Légende</strong>{' '}
          (jamais moins d'un cran, pour que la pression monte aussi tout en bas de l'échelle). Un nom discret voit la
          pression monter doucement ; une gloire des mers voit une armada de plus à chaque victoire. Tu as prouvé que tu
          encaisses, on t'envoie plus fort, au-delà même de ce que ta seule notoriété attirerait.
        </span>
        <span className="block mt-2">
          Cette escalade n'a <strong className="text-sand-200">plus de plafond</strong>&nbsp;: une forteresse qui ne plie
          jamais finit par voir venir de véritables armadas, puis des <strong className="text-sand-200">Légendaires</strong>{' '}
          (les coques les plus redoutables du jeu) jusqu'à ce que tu ne tiennes plus&nbsp;: la brèche la fait alors
          redescendre, et tu remontes. Ton point d'équilibre se cherche ainsi tout seul, autour de ce que tes murs valent
          vraiment. <strong className="text-sand-200">Plus dur, jamais plus punitif par coup</strong>&nbsp;: ce que tu
          risques de perdre sur une brèche reste, lui, toujours plafonné (voir plus bas) ; seule la difficulté grimpe sans
          limite. Le plancher de la force, lui, suit toujours le <strong className="text-sand-200">plus élevé</strong> de
          ta réputation et de ta propre <strong className="text-sand-200">puissance défensive</strong>. On ne s'acharne
          pas sur un capitaine déjà à terre.
        </span>
      </Note>
      <Note tone="info" Icon={(p) => <DefenseIcon type="battery" {...p} />} title="Le répit d'une brèche dépend de tes murs">
        <p>
          Le recul offert par une brèche n'est pas le même pour tout le monde, et c'est voulu. La force d'un assaut ne
          peut jamais descendre sous un <strong className="text-sand-200">plancher</strong> fixé par ta réputation et par
          la solidité de tes défenses. Autrement dit&nbsp;: <strong className="text-sand-200">plus tu es fortifié, moins
          une brèche te rend de répit</strong>, parce qu'une place forte reste une cible de choix même le lendemain d'un
          pillage.
        </p>
        <p className="mt-2">
          Le capitaine qui débute et se fait déborder récupère donc pleinement son recul, quand la citadelle qui plie une
          fois se relève quasiment au même niveau de menace. Et si ton plancher est déjà au-dessus de l'assaut que tu viens
          d'encaisser, la brèche ne change rien du tout&nbsp;: elle ne peut jamais{' '}
          <strong className="text-sand-200">augmenter</strong> la force du prochain raid.
        </p>
      </Note>
      <Note tone="danger" Icon={(p) => <DefenseIcon type="watchtower" {...p} />} title="Ta flotte à quai ne se bat pas d'elle-même">
        Par défaut, quand l'assaut débarque, <strong className="text-sand-200">seules tes structures défendent</strong>.
        Les navires amarrés restent surpris au mouillage&nbsp;: ils ne tirent pas un coup et peuvent être{' '}
        <strong className="text-sand-200">coulés sans avoir combattu</strong>. Une île sans défense est toujours pillée,
        d'où l'intérêt d'investir dans la pierre. Mais tu n'es plus condamné à regarder le pillage les bras croisés&nbsp;:
        si tu as <strong className="text-sand-200">sonné le branle-bas</strong> (voir juste en dessous), tes coques
        mobilisées prennent place dans la ligne de bataille derrière tes remparts. C'est un{' '}
        <strong className="text-sand-200">ordre que tu donnes</strong>, coûteux et temporaire&nbsp;; jamais un réflexe
        automatique de ta flotte.
      </Note>
      <Note tone="gold" Icon={CombatIcon} title="Le branle-bas de combat : faire monter ta flotte au rempart">
        <p>
          Tu le <strong className="text-sand-200">sonnes toi-même</strong> depuis ton panneau de défense, quand tu vois
          l'orage venir. Tant qu'il dure, tes navires à quai <strong className="text-sand-200">combattent aux côtés de
          tes structures</strong>, avec leurs modules et leurs bonus de Forge&nbsp;: le moteur de combat ne change pas,
          c'est simplement une flotte de plus dans la balance. Les pierres encaissent devant, les coques tirent derrière.
        </p>
        <p className="mt-2">
          Ton <strong className="text-sand-200">Chantier naval</strong> fixe combien de coques peuvent répondre à
          l'appel&nbsp;: la <strong className="text-sand-200">moitié de son niveau</strong> (au moins une). Un chantier de
          niveau&nbsp;16 range donc <strong className="text-sand-200">8 navires</strong> derrière tes murs, et ce sont tes{' '}
          <strong className="text-sand-200">coques les plus puissantes</strong> qui montent la garde en premier.
        </p>
        <p className="mt-2">
          Une garnison mange et se paie, et <strong className="text-sand-200">très cher</strong>. Le{' '}
          <RumIcon className="inline w-3.5 h-3.5 text-sand-300" /> nourrit les hommes&nbsp;: ta production s'arrête{' '}
          <strong className="text-sand-200">entièrement</strong> (l'île ne distille plus une goutte tant que le
          branle-bas dure) et chaque coque engagée creuse en plus la cave. L'{' '}
          <GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> paie la solde, sans toucher à ta production.
        </p>
        <p className="mt-2">
          Le tarif n'est <strong className="text-sand-200">pas un forfait</strong>&nbsp;: une coque engagée coûte, par
          heure, le plus lourd de deux prix, une <strong className="text-sand-200">part de ta production horaire</strong>{' '}
          ou <strong className="text-sand-200">1&nbsp;% de ta réserve</strong>. Deux conséquences à connaître avant de
          sonner. Une île prospère paie beaucoup plus qu'une petite, en rhum comme en or&nbsp;: le branle-bas d'un
          capitaine aguerri lui coûte une part de sa fortune, pas une pièce de menue monnaie. Et un{' '}
          <strong className="text-sand-200">gros stock dormant ne t'achète pas des jours de mobilisation</strong>, parce
          que c'est justement lui qu'on facture. À garnison pleine de légendaires, il faut compter environ{' '}
          <strong className="text-sand-200">une journée de production de rhum mise de côté</strong> pour avoir le droit
          de sonner, et deux journées de production d'or.
        </p>
        <p className="mt-2">
          Chaque rang paie selon sa puissance de feu, en part du tarif d'un légendaire&nbsp;:
        </p>
        <div className="mt-3 rounded-xl border border-sand-500/20 bg-ocean-900/30 p-2 overflow-x-auto">
          <table className="w-full min-w-[22rem] text-sm">
            <thead>
              <tr className="text-ocean-300">
                <th className="text-left font-normal px-3 py-2">Coque mobilisée</th>
                <th className="px-2 py-2 font-normal">
                  <span className="inline-flex flex-col items-center gap-1">
                    <span>Part du tarif</span>
                    <span className="text-[11px]">
                      en <RumIcon className="inline w-3 h-3 align-[-2px]" /> comme en{' '}
                      <GoldIcon className="inline w-3 h-3 align-[-2px]" />
                    </span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sand-500/10">
              {upkeep.map((u) => (
                <tr key={u.type}>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-2 text-sand-200 font-medium">
                      <ShipIcon type={u.type} className="w-5 h-5 text-sand-300" />
                      {u.name}
                    </span>
                  </td>
                  <td className="px-2 py-2.5 text-center tabular-nums text-ocean-100">{u.share}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3">
          Sonner le branle-bas <strong className="text-sand-200">engage pour 6&nbsp;heures</strong>, facturées d'avance
          quoi qu'il arrive. Ce n'est pas un interrupteur&nbsp;: tant que la fenêtre court, les rangs sont tenus et tu ne
          peux pas les rompre. On n'appelle pas l'équipage au poste de combat pour un quart d'heure, et surtout on ne
          mobilise pas une minute avant l'assaut pour renvoyer la flotte en mer juste après.
        </p>
        <p className="mt-2">
          Ce que tu risques&nbsp;: un navire qui se bat peut <strong className="text-sand-200">couler</strong>, comme en
          mission. Mais <strong className="text-sand-200">se battre ne te coûtera jamais plus de coques que de rester
          les bras croisés</strong>. Un assaut dispose d'un seul et même quota de destruction, qu'il t'arrache tes
          navires au mouillage ou qu'il les affronte&nbsp;: la garnison le consomme d'abord, le pillage n'hérite que du
          reste. Au-delà de ce quota, une coque tombée à ton propre port n'est pas perdue, elle est{' '}
          <strong className="text-sand-200">renflouée</strong>. Elle revient à quai en morceaux,{' '}
          <strong className="text-sand-200">hors d'état de naviguer</strong> et hors des rangs, jusqu'à ce que le
          carénage la remette à flot ou que tu paies le <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" /> de la
          réparation. Un branle-bas perdu se compte donc en semaines de chantier, plus en flotte rayée de la carte.
        </p>
        <p className="mt-2">
          Autre contrepartie&nbsp;: une coque mobilisée <strong className="text-sand-200">ne peut plus prendre la
          mer</strong> tant que le branle-bas dure, ni pour une mission, ni pour un convoi de contrebande, ni même pour
          un convoi de carrière. Défendre son port, c'est renoncer à naviguer.
        </p>
        <p className="mt-2">
          Deux façons d'en sortir. Tu <strong className="text-sand-200">romps les rangs</strong> une fois les six heures
          écoulées, ou la mobilisation <strong className="text-sand-200">s'arrête d'elle-même</strong> dès que l'une des
          deux ressources touche le fond&nbsp;: cave à sec ou caisse vide, l'équipage rentre chez lui et la cloche te
          prévient. Comme le <RumIcon className="inline w-3.5 h-3.5 text-sand-300" /> ne rentre plus pendant ce temps et
          que le tarif monte avec ta réserve, une garnison au complet ne tient jamais{' '}
          <strong className="text-sand-200">plus d'une demi-journée</strong>, aussi riche que tu sois. C'est un coup de
          poing, pas une posture.
        </p>
      </Note>
      <Note tone="info" Icon={PennantIcon} title="Qui t'attaque dépend de ton pavillon">
        L'assaillant suit ta <strong className="text-sand-200">réputation</strong> (axe Légal ↔ Hors-la-loi) : tant que
        tu restes <strong className="text-sand-200">loyal ou neutre</strong>, ce sont des <span className="whitespace-nowrap">🏴‍☠️ <strong className="text-sand-200">pirates</strong></span>{' '}
        qui convoitent ton repaire ; bascule <strong className="text-sand-200">hors-la-loi</strong> et c'est la{' '}
        <span className="whitespace-nowrap">⚓ <strong className="text-sand-200">Marine royale</strong></span> qui vient
        te traquer. Cet axe ne fixe que l'<em>identité</em> de l'ennemi&nbsp;: la <strong className="text-sand-200">force</strong>{' '}
        de l'assaut, elle, suit le <strong className="text-sand-200">plus élevé</strong> de ta réputation et de ce que tu
        as su <strong className="text-sand-200">repousser</strong> (voir plus haut). À renommée égale, Marine et pirates
        frappent aussi fort ; seul leur pavillon diffère.
      </Note>
      <Note tone="info" Icon={MapIcon} title="L'attaque s'annonce sur la carte">
        Un assaut en approche se voit&nbsp;: une <strong className="text-sand-200">ligne d'alerte pulsante</strong> relie
        l'<strong className="text-sand-200">horizon</strong>, d'où surgit l'ennemi, à ton repaire,{' '}
        <strong className="text-sand-200">colorée selon l'ennemi</strong>{' '}
        (<span className="whitespace-nowrap" style={{ color: '#6CA8FF' }}>bleu roi</span> pour la Marine,{' '}
        <span className="whitespace-nowrap" style={{ color: '#B0143C' }}>cramoisi</span> pour les pirates).{' '}
        <strong className="text-sand-200">Clique cette ligne</strong> pour ouvrir le détail de l'assaut&nbsp;:
        provenance (le large, par quel cap), horaire et danger. Tu retrouves aussi l'assaillant nommé dans le panneau de
        défense&nbsp;: de quoi renforcer tes structures avant qu'il n'accoste.
      </Note>
      <Note tone="info" Icon={(p) => <StatIcon {...p} name="abordage" />} title="Revivre chaque assaut, dé par dé">
        Chaque assaut résolu (pirate, Marine ou capitaine rival) reste consigné dans les{' '}
        <strong className="text-sand-200">Dernières attaques</strong> de ton panneau de défense, et tu peux y{' '}
        <strong className="text-sand-200">revivre le combat</strong>&nbsp;: le récit complet, dé par dé, vu de tes
        remparts, <strong className="text-sand-200">relances de ta Tour de guet</strong> comprises. (Les assauts
        encaissés avant l'apparition du récit n'en gardent pas trace.)
      </Note>
      <Note tone="info" Icon={(p) => <DefenseIcon type="watchtower" {...p} />} title="La Tour de guet dévoile l'assaut">
        Plus ta <strong className="text-sand-200">Tour de guet</strong> est haute, plus elle{' '}
        <strong className="text-sand-200">éclaire le prochain raid</strong>, et ce par paliers de niveau connus d'avance&nbsp;:
        la force de l'assaut ne change rien à ce qu'elle voit. Sans tour, tu ne connais que{' '}
        <strong className="text-sand-200">qui</strong> approche et un horaire <strong className="text-sand-200">flou</strong>{' '}
        (imminent / bientôt / lointain). Dès le <strong className="text-sand-200">niveau 1</strong> elle révèle la{' '}
        <strong className="text-sand-200">force</strong> de l'assaut&nbsp;; au{' '}
        <strong className="text-sand-200">niveau 6</strong> la <strong className="text-sand-200">composition de la flotte</strong>{' '}
        ennemie&nbsp;; au <strong className="text-sand-200">niveau 12</strong> une{' '}
        <strong className="text-sand-200">estimation de tes chances</strong> de la repousser&nbsp;; au{' '}
        <strong className="text-sand-200">niveau 18</strong> l'<strong className="text-sand-200">heure exacte</strong>{' '}
        d'arrivée. Et ça vaut
        autant pour un <strong className="text-sand-200">raid PNJ</strong> que pour un <strong className="text-sand-200">raid
        de joueur</strong> en approche ; ta vigie repère les deux. Élève la tour pour ne plus jamais être surpris à l'aveugle.
        {' '}Elle te défend aussi contre l'<strong className="text-sand-200">espionnage</strong>&nbsp;: plus elle est haute,
        plus elle a de chances de <strong className="text-sand-200">repousser un éclaireur</strong> ennemi venu relever tes
        défenses (il rentre bredouille, rien révélé). Et l'enjeu a grandi&nbsp;: une reconnaissance qui passe relève
        désormais le niveau de tes structures <em>et</em> la <strong className="text-sand-200">composition de ta flotte à
        quai</strong>, de quoi jauger ce que ton branle-bas mettrait dans la balance. Qu'il{' '}
        <strong className="text-sand-200">réussisse</strong> ou se fasse{' '}
        <strong className="text-sand-200">repousser</strong>, tu es <strong className="text-sand-200">prévenu nominativement</strong>,
        sauf le repli d'un sondeur sur une île <strong className="text-sand-200">protégée</strong> (bouclier / immunité), qui reste silencieux.
      </Note>
      <Note tone="gold" Icon={AnchorIcon} title="Des garde-fous anti-abus">
        Même vaincu, tu ne perds jamais tout : un <strong className="text-sand-200">bouclier de 4 jours</strong> protège
        les nouveaux capitaines, un raid réussi ne dérobe <strong className="text-sand-200">jamais plus de la moitié</strong>
        {' '}de chaque ressource, et les pertes de navires à quai sont plafonnées. Repousser un assaut redore ton blason
        auprès de la Marine, un geste d'<strong className="text-sand-200">alignement</strong>, sans te pousser vers la{' '}
        <strong className="text-sand-200">voie guerrière</strong>&nbsp;: un marchand peut défendre sans renier son négoce.
      </Note>
    </Section>
  )
}

function Reputation() {
  const factions: { pavillon: string; name: string; axis: string; how: ReactNode }[] = [
    { pavillon: '⚖️', name: 'Guilde marchande', axis: 'Légal · Marchand', how: <>Commerce honnête, achats aux comptoirs.</> },
    { pavillon: '⚓', name: 'Marine royale', axis: 'Légal · Guerrier', how: <>Chasse aux pirates, défense de ton île.</> },
    { pavillon: '🗡️', name: 'Corsaire', axis: 'Hors-la-loi · Marchand', how: <>Contrebande : vente au noir vers les repaires pirates.</> },
    { pavillon: '🏴‍☠️', name: 'Pirate', axis: 'Hors-la-loi · Guerrier', how: <>Pillage des ports honnêtes et des innocents. On commence par un comptoir de périphérie, on continue par ceux du centre, on finit sur les ports de la Marine.</> },
  ]
  return (
    <Section
      id="reputation"
      Icon={PennantIcon}
      title="Réputation : ton pavillon, tu le gagnes"
      lead={
        <>
          On ne choisit jamais son camp dans un menu : ce sont tes <em>actes</em> qui te placent sur deux axes,
          <strong className="text-sand-200"> Légal ↔ Hors-la-loi</strong> et <strong className="text-sand-200">Marchand ↔
          Guerrier</strong>. Le quadrant où tu penches décide de ta faction.
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        {factions.map((f) => (
          <div key={f.name} className="rounded-xl border border-sand-500/15 bg-ocean-900/30 p-4">
            <div className="flex items-center gap-2.5">
              <span className="text-2xl leading-none" aria-hidden="true">{f.pavillon}</span>
              <div className="min-w-0">
                <p className="font-display text-sand-200 leading-tight">{f.name}</p>
                <p className="text-[11px] uppercase tracking-wider text-ocean-300">{f.axis}</p>
              </div>
            </div>
            <p className="mt-2 text-xs text-ocean-100 leading-snug">{f.how}</p>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="info" Icon={GoldIcon} title="À quoi ça sert">
          Ton avantage <strong className="text-sand-200">grandit avec ta profondeur</strong> : nul au centre, il monte
          jusqu'à un plafond de maître. Côté marchand, ta faction colore les <strong className="text-sand-200">prix de
          vente</strong> (un pirate brade aux ports honnêtes mais fait fortune aux repaires pirates ; un marchand de la
          Guilde excelle aux ports officiels et aux comptoirs) et te fait <strong className="text-sand-200">acheter les ressources moins cher</strong>. À
          haut rang, marge de vente et remise d'achat se combinent assez pour qu'
          <strong className="text-sand-200">acheter ici et revendre là-bas</strong> soit une vraie route rentable : le
          négoce pur, pas seulement écouler ta production. Côté guerrier, ton{' '}
          <strong className="text-sand-200">butin de raid</strong> enfle. Un seul curseur : on ne peut pas
          être à fond marchand <em>et</em> guerrier.{' '}
          Côté <strong className="text-sand-200">hors-la-loi</strong> : piller le{' '}
          <strong className="text-sand-200">commerce mou</strong> (comptoirs et îles de production) rapporte une{' '}
          <strong className="text-sand-200">prime de butin</strong> qui grandit avec ta notoriété hors-la-loi. Les deux{' '}
          <strong className="text-sand-200">cibles-signatures</strong> des factions en sont exclues : chasser un repaire
          pirate (un acte légal) ne la touche pas, et donner l'assaut à une citadelle de la Marine non plus ; là, la
          récompense c'est l'or et le combat, pas la prime du contrebandier.
        </Note>
        <Note tone="gold" Icon={PennantIcon} title="Tant que tu hésites…">
          Au centre, tu restes <strong className="text-sand-200">Sans pavillon</strong> 🏳️. À mesure que tu t'affirmes,
          tu gravis les rangs : Novice, Confirmé, Notoire, Maître, jusqu'à Légende.
        </Note>
      </div>

      <Note tone="info" Icon={PennantIcon} title="Légende, ça se mérite, et ça s'entretient">
        Chaque saison, ta réputation <strong className="text-sand-200">repart de zéro</strong> (seul ton prestige de
        capitaine te suit). Et tout en haut, la <strong className="text-sand-200">notoriété d'une Légende s'érode</strong>{' '}
        si tu cesses d'agir, d'autant plus vite que tu es proche du sommet. Rien ne te fait jamais retomber{' '}
        <strong className="text-sand-200">sous Maître</strong> : les rangs inférieurs, eux, ne s'érodent pas. Pour tenir
        (et pousser) ta Légende, enchaîne les missions… et <strong className="text-sand-200">monte ta Taverne</strong> :
        plus elle est haute, plus tes quêtes rapportent de réputation. La recette du{' '}
        <strong className="text-sand-200">Faux-fond</strong>, qui s'ouvre au rang Notoire chez les hors-la-loi, suit la
        même règle : elle vaut pour la saison, et se re-mérite à la suivante.
      </Note>

      <div className="mt-2">
        <p className="text-[11px] uppercase tracking-[0.25em] text-ocean-300 flex items-center gap-2">
          <SkullIcon className="w-3.5 h-3.5 text-sand-300" /> La voie du corsaire : contrebande &amp; marché noir
        </p>
        <p className="mt-2 text-ocean-100 leading-relaxed max-w-3xl">
          Le marchand hors-la-loi a son propre négoce au <strong className="text-sand-200">repaire pirate</strong>,
          loin des routes honnêtes. Tu y montes un <strong className="text-sand-200">convoi de navires</strong> et tu
          choisis le sens du trafic, deux faces du même marché, mêmes acheteurs hors-la-loi :
        </p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <Mini Icon={GoldIcon} title="Contrebande : écouler ta cargaison">
            Tu charges les cales de <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" /> et de{' '}
            <RumIcon className="inline w-3.5 h-3.5 text-sand-300" /> et tu vas les vendre{' '}
            <strong className="text-sand-200">au noir</strong> : ta marchandise se paie une prime{' '}
            <strong className="text-sand-200">au-dessus du cours</strong>. Tu en ressors avec de l'{' '}
            <GoldIcon className="inline w-3.5 h-3.5 text-sand-300" />.
          </Mini>
          <Mini Icon={SkullIcon} title="Marché noir : racheter au rabais">
            Le sens inverse : tu <strong className="text-sand-200">dépenses ton or</strong> chez la même clientèle pour{' '}
            <strong className="text-sand-200">acheter du</strong> <RumIcon className="inline w-3.5 h-3.5 text-sand-300" />{' '}
            et du <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" /> à <strong className="text-sand-200">prix cassé</strong>,
            sous le manteau, sans repasser par un comptoir honnête. Un corsaire affirmé{' '}
            <strong className="text-sand-200">rachète sous le cours</strong> qu'il a écoulé en contrebande : le va-et-vient{' '}
            <strong className="text-sand-200">vendre haut / racheter bas</strong> finance ta progression, et alimente la{' '}
            <RumIcon className="inline w-3.5 h-3.5 text-sand-300" /> de la Forge et le{' '}
            <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" /> du chantier.
          </Mini>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <Note tone="info" Icon={GoldIcon} title="Le marché du corsaire affirmé">
            Le <strong className="text-sand-200">même barème</strong> joue dans les deux sens : la prime de vente comme
            la remise à l'achat grandissent avec ta <strong className="text-sand-200">notoriété hors-la-loi</strong> (ta
            profondeur Légal ↔ Hors-la-loi) et avec le <strong className="text-sand-200">danger de la cible</strong>.
            Le module <strong className="text-sand-200">Faux-fond</strong> joue lui aussi dans les deux sens : il fait
            vendre la contrebande <strong className="text-sand-200">plus cher</strong> et (à la mesure de ta notoriété
            hors-la-loi) acheter au marché noir <strong className="text-sand-200">moins cher</strong> ; le meilleur
            navire du convoi donne son taux à toute la cargaison.
            Le volume suit ta <strong className="text-sand-200">cale</strong> (grosses coques + grand convoi = davantage).
            Plus tu es craint et plus l'antre est périlleux, meilleur est le marché. Les deux sens te marquent du{' '}
            <strong className="text-sand-200">même pavillon</strong> : <strong className="text-sand-200">hors-la-loi</strong>{' '}
            et <strong className="text-sand-200">doctrine marchande</strong> (le pavillon Corsaire). Et pour les{' '}
            <strong className="text-sand-200">quêtes</strong>, un rachat au marché noir compte comme une{' '}
            <strong className="text-sand-200">contrebande réussie</strong> : il fait avancer les séries «&nbsp;sans te
            faire intercepter&nbsp;» au lieu de les briser.
          </Note>
          <Note tone="danger" Icon={(p) => <StatIcon {...p} name="manoeuvre" />} title="Le risque d'interception">
            Cette route n'est jamais sûre, dans un sens comme dans l'autre. Un{' '}
            <strong className="text-sand-200">tirage seedé</strong> (dont la probabilité monte, elle aussi, avec ta
            notoriété hors-la-loi et le danger de la cible) décide si une{' '}
            <span className="whitespace-nowrap">⚓ patrouille de la Marine</span> te prend en chasse. Si elle te
            repère, ça se règle aux dés : une <strong className="text-sand-200">vraie bataille navale</strong> (voir
            chapitre <em>Embuscades en mer</em>) où ton escorte peut{' '}
            <strong className="text-sand-200">forcer le blocus</strong> et sauver la cargaison. Si tu perds ce
            combat, tu rentres bredouille (cargaison confisquée en contrebande, ou{' '}
            <GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> englouti au marché noir) et tu{' '}
            <strong className="text-sand-200">perds un navire</strong> (au plus un, coulé définitivement ; les
            autres rentrent abîmés). Le marché est réel, le danger aussi : plus il est beau, plus le risque l'est.
          </Note>
        </div>
      </div>

      <div className="mt-2">
        <p className="text-[11px] uppercase tracking-[0.25em] text-ocean-300 flex items-center gap-2">
          <DiplomacyIcon className="w-3.5 h-3.5 text-sand-300" /> La table des négociations : diplomatie & corruption
        </p>
        <p className="mt-2 text-ocean-100 leading-relaxed max-w-3xl">
          L'or ouvre certaines portes. Une fois par <strong className="text-sand-200">24 h</strong>, tu peux t'asseoir
          à <strong className="text-sand-200">la table des négociations</strong> (menu Négociations) et verser une
          somme pour infléchir ton axe <strong className="text-sand-200">Légal ↔ Hors-la-loi</strong> de{' '}
          <strong className="text-sand-200">1 à 10 points</strong> : la <strong className="text-sand-200">Diplomatie</strong>{' '}
          (vers la Couronne) ou la <strong className="text-sand-200">Corruption</strong> (vers la Flibuste). Ta doctrine
          Marchand ↔ Guerrier, elle, ne se négocie jamais.
        </p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <Note tone="info" Icon={GoldIcon} title="Le prix de l'influence">
            Chaque point coûte de l'<GoldIcon className="inline w-3.5 h-3.5 text-sand-300" />, et{' '}
            <strong className="text-sand-200">d'autant plus que ton alignement est marqué</strong> : de 2 500 près du
            centre jusqu'à 7 500 vers ±300. Ta réputation te précède : creuser ton camp comme retourner ta veste se paie au
            prix fort.
          </Note>
          <Note tone="danger" Icon={PennantIcon} title="Tes actes restent ta réputation">
            L'or ne peut <strong className="text-sand-200">jamais</strong> pousser ton alignement au-delà de{' '}
            <strong className="text-sand-200">±300</strong> : Maître, Légende et les déblocages liés se gagnent par tes
            actes ; la table des négociations <strong className="text-sand-200">ajuste</strong> ton allégeance, elle ne
            la <strong className="text-sand-200">remplace</strong> pas.
          </Note>
        </div>
      </div>
    </Section>
  )
}

function Forge() {
  const branches: { Icon: Icon; name: string; what: string }[] = [
    { Icon: ModuleIcon, name: 'Modules', what: "Débloque les modules de navire avant de pouvoir les installer." },
    { Icon: (p) => <StatIcon {...p} name="bordee" />, name: 'Artillerie', what: 'Relance un dé de bordée raté, par type de coque.' },
    { Icon: (p) => <StatIcon {...p} name="abordage" />, name: 'Abordage', what: 'Relance un dé d\'abordage raté.' },
    { Icon: (p) => <StatIcon {...p} name="hp" />, name: 'Charpente', what: 'Renforce la coque : +1 point de vie par coque. Le bonus est inclus dans les PV affichés sur ta flotte (valeur en vert).' },
    { Icon: CompassIcon, name: 'Logistique', what: 'Cargo, vitesse, primes d\'exploration, temps de chantier.' },
  ]
  return (
    <Section
      id="forge"
      Icon={ForgeIcon}
      title="La Forge : l'arbre de progrès"
      lead="La Forge est ton arbre de recherche de la saison. Le rhum en est le grand débouché ; l'or et le bois ne coûtent qu'une fraction du prix en rhum. Tu ne pourras pas tout financer ; tes choix dessinent ta flotte."
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {branches.map((b) => (
          <Mini key={b.name} Icon={b.Icon} title={b.name}>{b.what}</Mini>
        ))}
      </div>
      <Note tone="info" Icon={RumIcon} title="Le grand débouché du rhum">
        Les relances ne déplacent jamais le seuil de touche : elles te donnent une seconde chance sur quelques dés
        ratés, un coup de pouce borné, jamais de la puissance brute. La Forge absorbe le surplus de rhum que ta
        distillerie laisse s'accumuler.
      </Note>
      <Note tone="info" Icon={CompassIcon} title="Une recherche prend du temps">
        Lancer une recherche n'est plus instantané : elle occupe la Forge pendant une durée proportionnelle à son
        coût en rhum (une seule à la fois). Tu la suis depuis le tableau de bord, et une notification t'avertit
        quand elle est terminée.
      </Note>
    </Section>
  )
}

function GrandChantier() {
  return (
    <Section
      id="chantier"
      Icon={LanternIcon}
      title="Le Grand Chantier : ériger le Grand Phare"
      lead={
        <>
          Quand il ne te reste plus rien à construire, il te reste une œuvre à bâtir. Le{' '}
          <strong className="text-sand-200">Grand Phare</strong> s'élève sur ton île en{' '}
          <strong className="text-sand-200">cinq paliers</strong>, avale des montagnes de ressources et ne rapporte
          ni or, ni bois, ni la moindre once de puissance : un titre, un ornement, du prestige et une place au
          classement. C'est une œuvre d'identité, pas une rampe de puissance.
        </>
      }
    >
      <Note tone="gold" Icon={LanternIcon} title="Il ne s'ouvre qu'en fin de partie, et il se referme à chaque saison">
        Deux conditions, et les deux sont nécessaires : ton{' '}
        <strong className="text-sand-200">Quartier-Général au niveau 15</strong> et l'objectif{' '}
        <strong className="text-sand-200">Maître-forgeron</strong> réclamé, soit dix recherches menées à la Forge.
        C'est délibérément haut : le Grand Chantier existe pour les capitaines qui produisent plus qu'ils ne peuvent
        dépenser. Les deux conditions sont <strong className="text-sand-200">saisonnières</strong> : au reset, le
        Quartier-Général repart du niveau 1 et Maître-forgeron se décoche, donc le portail est à rouvrir. C'est voulu,
        et c'est ce qui fait du Phare un ouvrage de saison plutôt qu'un acquis.
      </Note>

      <div className="grid sm:grid-cols-3 gap-4">
        <Mini Icon={LanternIcon} title="Cinq paliers">
          Les assises, le fût, la galerie, la lanterne, le couronnement. Un seul palier en chantier à la fois, et
          toujours dans l'ordre : on ne pose pas la lanterne avant le fût.
        </Mini>
        <Mini Icon={StoneIcon} title="D'abord la pierre">
          Chaque palier réclame un quota de <strong className="text-sand-200">pierres de taille</strong>, qu'il faut
          aller chercher à la carrière par convois. Le quota est prélevé à l'ouverture du palier : une fois payé, il
          est derrière toi.
        </Mini>
        <Mini Icon={HourglassIcon} title="Puis les versements">
          Un palier n'est pas payé d'un coup : c'est une souscription ouverte. Tu y verses quand tu veux, autant que
          tu veux, et la maçonnerie démarre au versement qui couvre le devis.
        </Mini>
      </div>

      <Note tone="info" Icon={ForgeIcon} title="Deux voies par palier, une seule à prendre">
        Chaque palier propose deux voies <strong className="text-sand-200">exclusives</strong>, choisies au lancement
        et définitives. Les deux boivent exactement le même{' '}
        <RumIcon className="inline w-3.5 h-3.5 text-sand-300" /> rhum : c'est la seule ressource qui n'a plus de
        destination en fin de partie, alors qu'or et bois partent déjà en coques et en bâtiments. Ce que la voie
        décide, c'est <strong className="text-sand-200">laquelle des deux ressources rares tu entames</strong>, et à
        quelle vitesse le chantier avance.
        <ul className="mt-2 space-y-1">
          <li>
            <strong className="text-sand-200">À la bourse et aux bras</strong> : tu ajoutes de l'
            <GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> au rhum, et le chantier avance vite.
          </li>
          <li>
            <strong className="text-sand-200">À la sueur et au bois</strong> : tu ajoutes du{' '}
            <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" /> au rhum, et le chantier avance lentement.
          </li>
        </ul>
        Les deux coûtent la même quantité, donc elles paient le même prestige. Cinq paliers, deux voies :{' '}
        <strong className="text-sand-200">trente-deux</strong> Grands Phares possibles, et le titre comme l'ornement
        gardent la trace du chemin pris.
      </Note>

      <Note tone="info" Icon={AnchorIcon} title="La carrière, et pourquoi ta cale compte enfin">
        Un convoi ramène exactement la <strong className="text-sand-200">capacité de charge de la flotte engagée</strong>,
        donc les cales renforcées et la recherche de cargaison de la Forge augmentent chaque voyage, tandis que le
        gréement et les voiles raccourcissent le trajet. Une flotte gréée pour la guerre transporte moins qu'une flotte
        gréée pour le fret : c'est un arbitrage réel avec la chasse légendaire, qui réclame l'inverse.
        <p className="mt-2">
          Un convoi de chantier <strong className="text-sand-200">ne peut pas être intercepté</strong> et ne rencontre
          aucun aléa : la pierre est déjà taillée, et perdre un chargement parce qu'un rival passait par là serait une
          punition sans décision. Un seul convoi en mer à la fois.
        </p>
      </Note>

      <Note tone="danger" Icon={HourglassIcon} title="Ce qui repart de zéro, ce qui reste gravé">
        L'ouvrage est <strong className="text-sand-200">saisonnier</strong> : au reset, le Grand Phare est à rebâtir
        entièrement, comme le reste du gameplay. Ce qu'il t'a rapporté, en revanche, ne bouge plus : le{' '}
        <strong className="text-sand-200">titre</strong> de chaque palier, l'
        <strong className="text-sand-200">ornement</strong> gagné en couronnant l'ouvrage et les{' '}
        <strong className="text-sand-200">points de prestige</strong> restent gravés sur ton compte, saison après
        saison.
        <p className="mt-2">
          Corollaire à connaître, et il coûte cher si tu l'apprends trop tard : la maçonnerie d'un palier doit
          pouvoir s'achever avant le reset. Un palier ne s'ouvre donc pas si la voie que tu choisis n'a plus le
          temps, et un palier déjà ouvert refuse tout nouveau versement dès que{' '}
          <strong className="text-sand-200">sa</strong> voie est à court, la voie étant figée à l'ouverture.
          Rien ne t'est rendu dans ce cas : ni le <strong className="text-sand-200">quota de pierre</strong>,
          prélevé au moment où tu ouvres le palier, ni les versements déjà faits. La voie de l'or étant la plus
          rapide, c'est elle qui reste ouverte le plus longtemps ; dans les derniers jours, celle du bois meurt
          la première. Ouvre tôt, ou ouvre par l'or.
        </p>
      </Note>

      <Note tone="gold" Icon={PennantIcon} title="Aucun bonus, jamais">
        Le Grand Phare n'améliore ni ta production, ni ton combat, ni ta vitesse, ni ton stockage. Il ne s'achète pas
        et ne se raccourcit pas. C'est ce qui le garde entièrement hors de l'équilibre du jeu :{' '}
        <strong className="text-sand-200">tout ce qu'il donne est cosmétique ou du score</strong>.
      </Note>
    </Section>
  )
}

function Objectifs() {
  return (
    <Section
      id="objectifs"
      Icon={ScrollIcon}
      title="Les objectifs : ta feuille de route"
      lead="Une échelle de jalons te guide, du premier chantier à la légende vivante. Toutes les voies y ont les leurs : bâtir, guerroyer, commercer (au grand jour comme en contrebande), écumer les récifs, achever des récits. Ils se valident tout seuls dès que tu atteins le but ; il ne te reste qu'à réclamer la récompense."
    >
      <div className="grid sm:grid-cols-3 gap-4">
        <ObjectiveSample title="Cartographe" goal="Explore 5 îles">
          <ResourceTally gold={200} className="text-sand-200" /> <Plus /> <PrestigeReward n={1} />
        </ObjectiveSample>
        <ObjectiveSample title="Maître de chantier" goal="Chantier niveau 2">
          <ResourceTally wood={300} className="text-sand-200" /> <Plus /> <PrestigeReward n={1} />
        </ObjectiveSample>
        <ObjectiveSample title="Légende vivante" goal="Atteins le rang Légende">
          <ResourceTally gold={3000} className="text-sand-200" /> <Plus /> <PrestigeReward n={10} />
        </ObjectiveSample>
      </div>
      <Note tone="gold" Icon={PrestigeIcon} title="Petites primes, grand renom">
        Les premiers jalons paient en ressources ; les plus exigeants paient en <strong className="text-sand-200">prestige</strong>,
        ce renom qui te suit de saison en saison.
      </Note>
      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="info" Icon={HourglassIcon} title="Saisonniers, à trois exceptions près">
          À chaque fin de saison, ta feuille de route se décoche et se rejoue : les compteurs qui la mesurent
          repartent de zéro, il serait absurde qu'elle reste cochée. Trois faits d'armes échappent à la règle, ceux
          qui paient un <strong className="text-sand-200">titre</strong> : Magnat du négoce, Maître cartographe et
          Fléau des capitaines. Ceux-là restent acquis, comme le titre qu'ils t'ont valu.
        </Note>
        <Note tone="gold" Icon={PrestigeIcon} title="Un fait d'armes refait paie une seconde fois">
          Un objectif re-gagné la saison suivante te repaie tout : ses récompenses en ressources, et son{' '}
          <strong className="text-sand-200">prestige</strong>. C'est le salaire du travail refait, et ton total de
          carrière monte d'autant. Dans une même saison, en revanche, un fait d'armes ne se réclame qu'une fois.
          Seuls les trois faits d'armes à titre, qui restent cochés à vie, ne se rejouent pas, donc ne repaient rien.
        </Note>
      </div>
    </Section>
  )
}

function Serie() {
  return (
    <Section
      id="serie"
      Icon={LanternIcon}
      title="Ta carte au trésor : une croix chaque jour"
      lead={
        <>
          Chaque jour actif <strong className="text-sand-200">trace une croix rouge</strong> sur ta{' '}
          <strong className="text-sand-200">carte au trésor</strong>, visible sur le tableau de bord ; la route
          pointillée montre les <strong className="text-sand-200">croix qu'il te reste à cocher</strong> jusqu'au
          trésor du prochain palier. À chaque palier franchi, une <strong className="text-sand-200">nouvelle
          série</strong> repart vers l'objectif suivant. Dans l'en-tête, la{' '}
          <strong className="text-sand-200">lanterne</strong> s'allume quand la croix du jour est tracée.
        </>
      }
    >
      <div className="grid sm:grid-cols-3 gap-4">
        <Mini Icon={CompassIcon} title="Une croix = une action">
          La croix du jour se trace dès que tu fais <strong className="text-sand-200">au moins une action</strong>{' '}
          dans la journée (heure de Paris) : lancer une mission, construire, négocier, réclamer une quête… Une
          simple visite ne suffit pas.
        </Mini>
        <Mini Icon={LifebuoyIcon} title="Les jours d'escale">
          Tous les 7 jours, tu gagnes <strong className="text-sand-200">1 jour d'escale</strong> (2 en réserve au
          maximum). Une escale couvre <strong className="text-sand-200">automatiquement</strong> un jour manqué :
          ta route continue. Sans escale suffisante, la carte repart du 1ᵉʳ jour, et la réserve se vide avec elle.
        </Mini>
        <Mini Icon={PrestigeIcon} title="Des trésors qui honorent">
          Les trésors du 1ᵉʳ et du 3ᵉ jour paient un coup de pouce en ressources ; ceux des 7ᵉ, 14ᵉ et 30ᵉ jours
          paient en <strong className="text-sand-200">prestige</strong> et en{' '}
          <strong className="text-sand-200">titres</strong> (« Loup de mer », « Habitué du bord »,
          « Fidèle de Corsalia ») ; ensuite, +2 prestige tous les 15 jours, sans fin. Le trésor tombe à ta
          première action du jour du palier ; le <strong className="text-sand-200">coffre s'illumine</strong> sur
          la carte pour marquer la prise.
        </Mini>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="gold" Icon={PrestigeIcon} title="Les titres sont permanents">
          Les titres de la carte au trésor appartiennent à ton <strong className="text-sand-200">compte</strong> :
          ils survivent aux saisons et s'affichent sur ton livret de bord (privé comme public), à côté de ton
          pavillon. La carte elle-même est <strong className="text-sand-200">saisonnière</strong> : compteur et
          escales repartent de zéro au reset, et les trésors se rejouent à chaque nouvelle carte.
        </Note>
        <Note tone="info" Icon={HourglassIcon} title="Aucune pression, aucun paiement">
          Le jeu reste asynchrone : une action par jour suffit, les escales absorbent les imprévus, et tout se
          gagne <strong className="text-sand-200">en jouant</strong> ; la carte au trésor n'a aucun lien avec le
          premium.
        </Note>
      </div>
    </Section>
  )
}

function Taverne() {
  return (
    <Section
      id="taverne"
      Icon={(p) => <BuildingIcon type="tavern" {...p} />}
      title="La Taverne : le tableau des quêtes"
      lead={
        <>
          La Taverne est un <strong className="text-sand-200">bâtiment à construire</strong> sur ton île ;
          elle n'est pas là à la fondation. Son coût de construction et d'amélioration se paie
          uniquement en <GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> or.
          Sans Taverne, <strong className="text-sand-200">aucune quête n'est disponible</strong> : c'est elle
          qui fait tourner le tableau des offres.
        </>
      }
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="info" Icon={HourglassIcon} title="Cadence des nouvelles offres">
          Une <strong className="text-sand-200">seule nouvelle offre</strong> apparaît à chaque fenêtre de
          cadence ; l'intervalle dépend du niveau de ta Taverne :
          <ul className="mt-2 space-y-1 text-xs text-ocean-100 list-none pl-0">
            <li>N1-N2 : toutes les <strong className="text-sand-200">36&nbsp;h</strong></li>
            <li>N3-N4 : toutes les <strong className="text-sand-200">24&nbsp;h</strong></li>
            <li>N5-N6 : toutes les <strong className="text-sand-200">12&nbsp;h</strong></li>
            <li>N7-N8 : toutes les <strong className="text-sand-200">10&nbsp;h</strong></li>
            <li>N9+ : toutes les <strong className="text-sand-200">8&nbsp;h</strong></li>
          </ul>
        </Note>
        <Note tone="info" Icon={(p) => <BuildingIcon type="tavern" {...p} />} title="Offres affichées & affaires actives">
          Le nombre d'offres simultanées sur le tableau et de quêtes actives en parallèle grimpe
          avec le niveau (départ 1/1 au N1, +1 alterné par niveau) :{' '}
          <strong className="text-sand-200">jusqu'à 5 offres et 5 actives au niveau 9</strong>.
        </Note>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5">
          <div className="flex items-center gap-2.5 mb-2">
            <span className="grid place-items-center w-9 h-9 shrink-0 rounded-lg bg-ocean-900/55 border border-sand-500/25 text-sand-300">
              <TreasureMapIcon className="w-5 h-5" />
            </span>
            <h3 className="font-display text-base text-sand-200 leading-tight">Familles débloquées par niveau</h3>
          </div>
          <ul className="text-sm text-ocean-100 space-y-1 leading-relaxed list-none p-0">
            <li><strong className="text-sand-200">N1</strong> : quêtes communes (toutes factions)</li>
            <li><strong className="text-sand-200">N2</strong> : quêtes PvP (rang ≥ 1 toujours requis)</li>
            <li><strong className="text-sand-200">N3</strong> : quêtes narratives à étapes</li>
            <li><strong className="text-sand-200">N5</strong> : quêtes rares / chasses au trésor</li>
          </ul>
        </div>
        <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5">
          <div className="flex items-center gap-2.5 mb-2">
            <span className="grid place-items-center w-9 h-9 shrink-0 rounded-lg bg-ocean-900/55 border border-sand-500/25 text-sand-300">
              <RumIcon className="w-5 h-5" />
            </span>
            <h3 className="font-display text-base text-sand-200 leading-tight">Actions au rhum</h3>
          </div>
          <p className="text-sm text-ocean-100 leading-relaxed">
            Deux actions payantes en <RumIcon className="inline w-3.5 h-3.5 text-sand-300" /> rhum :{' '}
            <strong className="text-sand-200">reroller une offre</strong> (coût croissant 60 / 120 / 240 / 480 rhum,
            4&nbsp;max par jour UTC, remis à zéro à minuit) et{' '}
            <strong className="text-sand-200">payer une tournée</strong> (N5+, 400&nbsp;rhum, cooldown 24&nbsp;h) qui
            fait apparaître immédiatement une offre rare. Le reroll ne vaut que pour les{' '}
            <strong className="text-sand-200">offres du tableau</strong>&nbsp;: une trouvaille (bouteille à la mer,
            offre de tournée) ne se reroll pas.
          </p>
        </div>
      </div>
      <Note tone="info" Icon={TreasureMapIcon} title="Chasses au trésor">
        Les quêtes rares incluent des <strong className="text-sand-200">chasses au trésor</strong> : des quêtes à
        étapes dont l'une cible une <strong className="text-sand-200">île précise que tu as déjà découverte</strong>.
        La destination est dévoilée à l'acceptation, et l'étape concernée{' '}
        <strong className="text-sand-200">nomme l'île</strong> à viser : l'action (vendre sur place, par exemple) ne
        compte que <strong className="text-sand-200">si elle s'y déroule</strong> ; la mener ailleurs ne fait pas
        avancer la quête.
      </Note>
      <Note tone="info" Icon={BottleIcon} title="Quêtes surprise : hors plafond">
        Certaines quêtes peuvent surgir <strong className="text-sand-200">hors du tableau</strong> pendant une
        mission : une bouteille à la mer, une carte retrouvée dans les filets… Ces offres{' '}
        <strong className="text-sand-200">ne comptent pas dans le plafond d'affaires actives</strong> : tu peux les
        accepter même si toutes tes créneaux habituels sont pris.
      </Note>
    </Section>
  )
}

function Quetes() {
  return (
    <Section
      id="quetes"
      Icon={TreasureMapIcon}
      title="Les quêtes : des offres à saisir ou à laisser"
      lead={
        <>
          Là où les objectifs sont des jalons <strong className="text-sand-200">permanents</strong> qui se valident
          tout seuls, les quêtes sont des <strong className="text-sand-200">offres limitées dans le temps</strong> que
          tu choisis d'<strong className="text-sand-200">accepter ou de refuser</strong>. Le tableau est{' '}
          <strong className="text-sand-200">entièrement régi par ta Taverne</strong> (voir chapitre{' '}
          <em>La Taverne</em>) : cadence, nombre d'offres et familles disponibles dépendent de son niveau.
        </>
      }
    >
      <div className="grid sm:grid-cols-3 gap-4">
        <Mini Icon={PennantIcon} title="Thémées par ton pavillon">
          Une quête porte les couleurs d'une faction : Guilde, Marine, Corsaire ou Pirate (ou reste neutre). On te
          propose celles qui collent à la voie que tu empruntes, jamais un camp imposé.
        </Mini>
        <Mini Icon={CompassIcon} title="On les remplit en jouant">
          Pas de bouton magique. Une quête avance avec tes <strong className="text-sand-200">vrais actes</strong> :
          commerce (acheter, vendre), pillage de repaire, contrebande, repousser un assaut,{' '}
          <strong className="text-sand-200">achever un récit</strong> d'île mystérieuse, ou simplement mener des
          missions. Les objectifs <strong className="text-sand-200">explorer</strong> comptent aussi la{' '}
          <strong className="text-sand-200">reconnaissance d'une île ennemie</strong> (scouting PvP) et la{' '}
          <strong className="text-sand-200">récupération sur un récif</strong> (certaines quêtes ciblent
          spécifiquement un récif), utile une fois tout l'archipel découvert.
        </Mini>
        <Mini Icon={TreasureMapIcon} title="Simple, multiple ou en chaîne">
          Une quête peut tenir en une seule tâche, en regrouper plusieurs à mener de front, ou enchaîner des{' '}
          <strong className="text-sand-200">étapes dans l'ordre</strong> : une chaîne où chaque palier ouvre le suivant.
        </Mini>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="info" Icon={HourglassIcon} title="Une échéance court dès l'acceptation">
          Tant qu'une offre dort sur la table, rien ne presse. Mais une fois acceptée, la quête a une{' '}
          <strong className="text-sand-200">échéance (72&nbsp;h en général)</strong> : passé ce délai, elle{' '}
          <strong className="text-sand-200">expire</strong>, sans pénalité, mais sans récompense non plus.
          Tu peux aussi <strong className="text-sand-200">abandonner</strong> une quête active à tout moment
          (bouton <em>Abandonner</em> sur la carte) : elle est retirée sans pénalité et libère immédiatement
          ton slot d'affaires actives.
        </Note>
        <Note tone="gold" Icon={GoldIcon} title="Récompense au choix : or ou bois">
          Une quête menée à bien paie en ressources + un gain de{' '}
          <strong className="text-sand-200">réputation</strong> qui te pousse vers sa faction. La plupart des quêtes
          proposent un <strong className="text-sand-200">choix au moment de réclamer</strong> :{' '}
          prendre la récompense en <GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> or ou en{' '}
          <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" /> bois (montant légèrement inférieur).
          Le rhum n'est jamais un choix.{' '}
          <strong className="text-sand-200">Jamais de prestige, jamais d'avantage payant</strong> : fidèle à la règle
          anti-pay-to-win.
        </Note>
      </div>
      <Note tone="info" Icon={ScrollIcon} title="La quête Ascension">
        La quête généraliste <strong className="text-sand-200">Ascension</strong> te demande de{' '}
        <strong className="text-sand-200">gagner un rang de réputation</strong>. Elle disparaît du tableau
        une fois que tu as atteint le rang maximum ; il n'y a plus de palier à franchir.
      </Note>
      <Note tone="info" Icon={TreasureMapIcon} title="Où les suivre">
        Tout se passe sur la page <strong className="text-sand-200">Taverne</strong> (lien dans la barre de navigation) :
        les offres à accepter, celles en cours, et celles dont la récompense t'attend. Une{' '}
        <strong className="text-sand-200">pastille</strong> s'allume dès qu'une récompense est prête à être réclamée, et{' '}
        <strong className="text-sand-200">accomplir une quête te prévient à la cloche</strong> que ta récompense
        t'attend. Sur une quête en cours, le tableau t'indique aussi où accomplir l'étape courante
        (un raccourci vers la carte), et signale si une mission qui la validerait est déjà en mer.
      </Note>
    </Section>
  )
}

function Prestige() {
  return (
    <Section
      id="prestige"
      Icon={PrestigeIcon}
      title="Le prestige : le renom qui traverse les saisons"
      lead={
        <>
          À chaque fin de saison, le gameplay est remis à zéro, mais ton <strong className="text-sand-200">prestige</strong>,
          lui, demeure. C'est la mémoire de ce que ton capitaine a accompli, accumulée d'une saison à l'autre.
        </>
      }
    >
      <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5">
        <p className="text-[11px] uppercase tracking-[0.25em] text-ocean-300">D'où il vient</p>
        <p className="mt-2 text-ocean-100 text-sm leading-relaxed">
          Le prestige ne paie que ce que tu <strong className="text-sand-200">accomplis</strong>. Quatre sources, et
          quatre seulement : chaque <strong className="text-sand-200">fait d'armes</strong> réclamé (les quarante réunis
          valent 84 points, dont 66 se rejouent à chaque saison où tu les refais), chaque <strong className="text-sand-200">palier du Grand Phare</strong> érigé
          (<PrestigeReward n={25} /> pour la chaîne entière), les <strong className="text-sand-200">paliers de ta
          série</strong> (<PrestigeReward n={1} /> au 7ᵉ jour, <PrestigeReward n={2} /> au 14ᵉ, <PrestigeReward n={5} /> au
          30ᵉ, puis <PrestigeReward n={2} /> tous les 15 jours), et l'abattage d'une{' '}
          <strong className="text-sand-200">bête légendaire</strong> (<PrestigeReward n={25} />).
          Chaque point est crédité sur le coup, et ne repart jamais.
        </p>
      </div>
      <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5">
        <p className="text-[11px] uppercase tracking-[0.25em] text-ocean-300">Ce que la fin de saison en verse : rien</p>
        <p className="mt-2 text-ocean-100 text-sm leading-relaxed">
          Le couperet ne crédite <strong className="text-sand-200">aucun</strong> prestige. Ni tes bâtiments, ni ta
          flotte, ni tes réserves du dernier jour ne pèsent quoi que ce soit dans ton renom : on ne compte pas ce que tu
          possédais, on compte ce que tu as fait. Tout ce que tu gagnes, tu l'as gagné en cours de route : un capitaine
          qui abat la bête et couronne son Phare emporte 50 points sur ces deux exploits, et le reste vient de ses
          faits d'armes et de sa série.
        </p>
      </div>
      <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5">
        <p className="text-[11px] uppercase tracking-[0.25em] text-ocean-300">Le palmarès des saisons closes</p>
        <p className="mt-2 text-ocean-100 text-sm leading-relaxed">
          Au couperet, le <strong className="text-sand-200">podium de chacun des quatre tableaux saisonniers</strong>{' '}
          est gravé pour de bon : Fortune, Terreur des mers, Maîtres du négoce et Grand Chantier. Les trois premiers de
          chaque tableau reçoivent un <strong className="text-sand-200">titre de podium</strong> au nom de la saison,
          qui rejoint ta cabine et n'en repart jamais. Une place que personne n'a disputée reste vacante, et un tableau
          que nul n'a marqué n'y laisse aucun nom : mieux vaut une place vide qu'un premier sacré à zéro point. Le
          palmarès se consulte dans l'onglet{' '}
          <strong className="text-sand-200">Saisons passées</strong> des classements, qui s'ouvre à la première
          clôture : tant qu'aucune saison n'est close, il n'y a rien à y lire et il ne s'affiche pas. Tes propres
          places figurent sur ton livret de bord.
        </p>
      </div>
      <Note tone="gold" Icon={PrestigeIcon} title="Mérité, jamais acheté">
        Le tableau des <strong className="text-sand-200">Légendes</strong> classe déjà le prestige de toutes les
        saisons confondues, et le palmarès grave les podiums de celles qui sont closes. Les{' '}
        <strong className="text-sand-200">bonus de fidélité</strong> adossés au prestige, eux, restent{' '}
        <strong className="text-sand-200">à venir</strong> : ils seront gagnés à la sueur du pont, jamais achetés à la
        boutique.
      </Note>
    </Section>
  )
}

function Notifications() {
  return (
    <Section
      id="notifications"
      Icon={ParrotIcon}
      title="Notifications : ton journal de bord"
      lead={
        <>
          Comme tout se joue <strong className="text-sand-200">hors-ligne</strong>, l'absence ne doit pas
          être un trou noir. Ton perroquet de bord, en haut de l'écran, garde la trace de ce qui s'est passé
          pendant que tu n'étais pas là.
        </>
      }
    >
      <div className="grid sm:grid-cols-3 gap-4">
        <Mini Icon={CompassIcon} title="Missions terminées">
          Chaque expédition rentrée laisse une notification : son issue, le butin, les pertes éventuelles.
        </Mini>
        <Mini Icon={DefenseShield} title="Attaques subies">
          Un raid résolu pendant ton absence, pirate PNJ <em>ou</em> capitaine rival ? Tu retrouves le bilan des deux
          côtés : défenses tenues ou île pillée, butin ou épave récupérée.
        </Mini>
        <Mini Icon={TargetIcon} title="Espionnée">
          Quand un <strong className="text-sand-200">éclaireur</strong> ennemi relève tes défenses, la cloche t'en
          prévient : tu sais qu'un capitaine te jauge.
        </Mini>
      </div>
      <Note tone="gold" Icon={ParrotIcon} title="Live + persistant">
        Les nouveautés s'affichent en direct (badge non-lus) et restent consultables au retour. Un clic
        déplie le détail complet <em>et</em> marque la notification lue d'un coup.{' '}
        <strong className="text-sand-200">À venir</strong> : les notifications push pour être prévenu hors de l'onglet.
      </Note>
    </Section>
  )
}

function Pvp() {
  return (
    <Section
      id="pvp"
      Icon={TargetIcon}
      title="Le PvP : capitaine contre capitaine"
      lead={
        <>
          Le grand large a désormais un adversaire bien vivant : les <strong className="text-sand-200">autres
          capitaines</strong>. Sur la carte, leurs îles affichent leur <strong className="text-sand-200">pavillon</strong>
          {' '}(la faction que leurs actes dessinent) et leur <strong className="text-sand-200">rang de notoriété</strong>.
          Clique sur l'une d'elles : tu peux y envoyer un <strong className="text-sand-200">éclaireur</strong> ou lancer
          un <strong className="text-sand-200">raid</strong>.
        </>
      }
    >
      <div className="grid sm:grid-cols-3 gap-4">
        <Mini Icon={CompassIcon} title="L'éclaireur : espionner d'abord">
          Une <strong className="text-sand-200">reconnaissance</strong> s'envoie en{' '}
          <strong className="text-sand-200">flotte d'éclaireurs</strong> (1 à 5 navires). Si elle <strong className="text-sand-200">passe</strong>,
          elle te révèle les <strong className="text-sand-200">défenses et le danger</strong> de la cible, la{' '}
          <strong className="text-sand-200">composition de sa flotte à quai</strong> (ce qu'elle pourrait mobiliser contre
          toi), et débloque sur
          l'écran de raid une <strong className="text-sand-200">estimation de tes chances de victoire et de tes pertes</strong>
          {' '}pour la flotte que tu prépares. Mais la <strong className="text-sand-200">tour de guet</strong> adverse peut
          la <strong className="text-sand-200">repérer et la repousser</strong> bredouille : chaque{' '}
          <strong className="text-sand-200">Longue-vue</strong> (module débloqué à la Forge) embarquée ajoute sa portée ;
          la <strong className="text-sand-200">profondeur de reconnaissance se cumule</strong> sur la flotte, donc plus tu
          envoies d'éclaireurs équipés, plus tu vois loin face à une tour haute. Sans reconnaissance, tu attaques à
          l'aveugle. Et le renseignement est <strong className="text-sand-200">figé
          à l'instant du relevé</strong> : la cible a pu se renforcer depuis ; un repère « <strong className="text-sand-200">intel du
          JJ/MM</strong> » te rappelle qu'il peut être périmé. Attention : la cible est toujours{' '}
          <strong className="text-sand-200">prévenue nominativement</strong> dès qu'un éclaireur{' '}
          <strong className="text-sand-200">réussit</strong> à relever ses défenses ; en cas de{' '}
          <strong className="text-sand-200">repli</strong>, elle est aussi prévenue, <em>sauf</em> si elle est{' '}
          <strong className="text-sand-200">protégée</strong> (bouclier nouveau-venu / immunité post-pillage).
        </Mini>
        <Mini Icon={CombatIcon} title="Le raid : surprendre, ou tomber sur un port en alerte">
          Tu engages une <strong className="text-sand-200">flotte de navires à quai</strong> contre l'île adverse. Comme
          pour les raids PNJ, la défense repose d'abord sur les <strong className="text-sand-200">structures</strong>
          {' '}(fort, batterie, milice, tour de guet), et la flotte ennemie <strong className="text-sand-200">à quai et
          inactive</strong> reste surprise au mouillage, donc coulable sans un coup de canon (celle{' '}
          <strong className="text-sand-200">en mission</strong> est hors de ta portée). Mais si la cible a{' '}
          <strong className="text-sand-200">sonné le branle-bas de combat</strong>, ses coques t'attendent en ligne de
          bataille et rendent les coups&nbsp;; mieux vaut l'avoir appris de ta{' '}
          <strong className="text-sand-200">reconnaissance</strong> avant de lever l'ancre. Une fois lancé, le raid{' '}
          <strong className="text-sand-200">fait route en temps
          réel</strong> (comme une mission) : tu suis son <strong className="text-sand-200">tracé sur la carte</strong>, son
          compte à rebours au <strong className="text-sand-200">tableau de bord</strong> et dans le{' '}
          <strong className="text-sand-200">carnet de raids</strong> ; à la résolution, le bilan (et le replay du combat)
          t'y attend.
        </Mini>
        <Mini Icon={PennantIcon} title="Plancher de force (anti-bully)">
          Tu ne peux pas fondre sur un capitaine <strong className="text-sand-200">bien plus faible</strong> que toi : le
          jeu refuse les proies trop modestes. En revanche, rien ne t'empêche de <strong className="text-sand-200">défier
          plus fort</strong> que toi.
        </Mini>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="gold" Icon={GoldIcon} title="Le butin : jusqu'à 25 % du trésor">
          Un raid victorieux dérobe au défenseur <strong className="text-sand-200">25&nbsp;%</strong> de{' '}
          <ResourceTally gold={1} wood={1} rhum={1} className="text-sand-200" /> ; son{' '}
          <strong className="text-sand-200">coffre-fort garde les 75&nbsp;%</strong> restants, et ton butin reste
          plafonné par ton entrepôt. À l'inverse, un défenseur qui <strong className="text-sand-200">repousse</strong>
          {' '}l'assaut récupère l'<strong className="text-sand-200">épave</strong> (
          <GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> <WoodIcon className="inline w-3.5 h-3.5 text-sand-300" />)
          des navires ennemis coulés.
        </Note>
        <Note tone="info" Icon={PennantIcon} title="Réputation : ton pavillon te suit">
          Réussir une attaque monte ta <strong className="text-sand-200">doctrine guerrière</strong> (la méthode), et
          fait glisser ton <strong className="text-sand-200">alignement selon la faction de la cible</strong> : piller un
          <strong className="text-sand-200"> loyal</strong> te pousse vers <span className="whitespace-nowrap">🏴‍☠️ Pirate</span>,
          piller un <strong className="text-sand-200">hors-la-loi</strong> vers <span className="whitespace-nowrap">⚓ Marine</span>,
          une cible <strong className="text-sand-200">neutre</strong> ne décale rien. Le défenseur, lui, ne gagne aucune
          réputation. (Voir le chapitre <em>Réputation</em>.)
        </Note>
      </div>

      <Note tone="info" Icon={TreasureMapIcon} title="Quêtes & objectifs PvP">
        Les <strong className="text-sand-200">quêtes PvP</strong> font monter ta{' '}
        <strong className="text-sand-200">doctrine guerrière</strong> (méthode) <Plus />{' '}
        <ResourceTally gold={1} wood={1} rhum={1} className="text-sand-200" /> des ressources, mais{' '}
        <strong className="text-sand-200">jamais d'alignement</strong> (lui ne vient que de <em>qui</em> tu attaques).
        Côté <strong className="text-sand-200">objectifs</strong>, des paliers (victoires remportées, assauts repoussés,
        butin pillé) se débloquent en ressources <Plus /> <strong className="text-sand-200">prestige</strong>.
      </Note>

      <Note tone="gold" Icon={AnchorIcon} title="Anti-grief : des garde-fous en dur">
        Le PvP reste bordé : <strong className="text-sand-200">bouclier de 4&nbsp;jours</strong> pour les nouveaux
        capitaines (ni attaquer ni être attaqué), <strong className="text-sand-200">3 raids maximum par 24&nbsp;h</strong>
        {' '}sur une même cible, et <strong className="text-sand-200">24&nbsp;h d'immunité</strong> après un pillage subi.
        Personne ne se fait acharner. Cette immunité vaut aussi contre une flotte{' '}
        <strong className="text-sand-200">déjà en mer</strong> : si un autre capitaine pille ta cible pendant ta
        traversée, ton escadre fait <strong className="text-sand-200">demi-tour sans combattre</strong>. Le rhum de
        rationnement et le temps du trajet sont perdus, les coques rentrent toutes au port, et les deux capitaines
        reçoivent le rapport.
      </Note>

      <Note tone="wip" Icon={StormIcon} title="À venir">
        L'<strong className="text-sand-200">interception</strong> de navires en transit (croiser le fer en pleine mer,
        hors de toute île) et le <strong className="text-sand-200">mode vacances</strong> sont encore au chantier ; pour
        l'heure, le PvP se joue d'île à île.
      </Note>
    </Section>
  )
}

function Classements() {
  return (
    <Section
      id="classements"
      Icon={CrownIcon}
      title="Classements : graver son nom"
      lead={
        <>
          Cinq tableaux pour briller. Chaque capitaine y est classé par ses{' '}
          <strong className="text-sand-200">actes</strong> ; rien ne s'y achète.
        </>
      }
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <Mini Icon={CrownIcon} title="Fortune">
          Ton essor de la saison : richesse, conquêtes et renommée confondues.
        </Mini>
        <Mini Icon={CombatIcon} title="Terreur des mers">
          Le butin arraché en mer cette saison : <strong className="text-sand-200">abordages de capitaines</strong> et{' '}
          <strong className="text-sand-200">raids de repaires PNJ</strong>, comptés en équivalent-or. Un sous-filtre le
          ventile par pavillon : <strong className="text-sand-200">Marine</strong> (chasse aux hors-la-loi) ou{' '}
          <strong className="text-sand-200">Pirate</strong> (pillage des loyaux).
        </Mini>
        <Mini Icon={GoldIcon} title="Maîtres du négoce">
          L'<GoldIcon className="inline w-3.5 h-3.5 text-sand-300" /> or du négoce cette saison :{' '}
          <strong className="text-sand-200">commerce honnête</strong> (Guilde) et{' '}
          <strong className="text-sand-200">contrebande</strong> (Corsaire) confondus, filtrables par pavillon.
        </Mini>
        <Mini Icon={PrestigeIcon} title="Légendes">
          Le <strong className="text-sand-200">prestige cumulé de toujours</strong>, le seul tableau qui traverse
          les saisons.
        </Mini>
        <Mini Icon={LanternIcon} title="Grand Chantier">
          Les <strong className="text-sand-200">paliers du Grand Phare</strong> érigés cette saison. À nombre égal de
          paliers, c'est la <strong className="text-sand-200">date</strong> qui départage&nbsp;: le premier à avoir
          dressé son palier passe devant, et rien de ce qu'un rival versera ensuite ne lui reprendra sa place.
        </Mini>
      </div>
      <Note tone="info" Icon={PennantIcon} title="Filtrer par pavillon">
        Terreur des mers et Maîtres du négoce se filtrent par <strong className="text-sand-200">alignement de
        l'acte</strong> : Tout, loyal ou hors-la-loi. Le classement additionne alors le seul butin des prises de ce
        bord : chasser les repaires pirates (⚓ Marine) et piller les loyaux (☠ Pirate) comptent chacun de leur côté,
        et un même capitaine peut figurer <strong className="text-sand-200">dans les deux</strong> s'il a joué sur les
        deux tableaux.
      </Note>
      <Note tone="gold" Icon={HourglassIcon} title="Quatre tableaux remis à zéro, un éternel">
        Fortune, Terreur des mers, Maîtres du négoce et Grand Chantier{' '}
        <strong className="text-sand-200">se réinitialisent à chaque nouvelle saison</strong> : tout le monde repart à
        égalité. Seul le tableau des <strong className="text-sand-200">Légendes</strong> survit aux saisons ; il grave
        le prestige accumulé d'une saison à l'autre. Rien n'est perdu pour autant : à la clôture, le podium des quatre
        tableaux saisonniers est archivé dans un onglet <strong className="text-sand-200">Saisons passées</strong> qui
        apparaît alors dans les classements (faute de prétendant, une place s'y lit sans nom), et ses trois premiers
        gardent un titre.
      </Note>
    </Section>
  )
}

function ProfilPublic() {
  return (
    <Section
      id="profil-public"
      Icon={PennantIcon}
      title="Profil public : le livret de bord d'un capitaine"
      lead={
        <>
          Chaque capitaine a un <strong className="text-sand-200">livret de bord public</strong> : son{' '}
          <strong className="text-sand-200">pavillon</strong> (réputation), son{' '}
          <strong className="text-sand-200">prestige</strong> et ses{' '}
          <strong className="text-sand-200">faits d'armes</strong>.
        </>
      }
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <Note tone="info" Icon={ScrollIcon} title="Le « Registre de la saison »">
          On y lit ses <strong className="text-sand-200">exploits de la saison en cours</strong> : butin arraché,
          gains du commerce, raids gagnés et repoussés, îles découvertes (du{' '}
          <strong className="text-sand-200">flux</strong>, ce qu'il a accompli).
        </Note>
        <Note tone="gold" Icon={DefenseShield} title="Ce qui reste secret">
          <strong className="text-sand-200">Jamais</strong> ses réserves, sa flotte ni ses défenses : ce qui pourrait
          servir un assaillant reste <strong className="text-sand-200">caché</strong> (anti-grief). Le livret expose le
          renom, pas le coffre.
        </Note>
      </div>
      <Note tone="info" Icon={CrownIcon} title="Comment y accéder">
        Clique un capitaine dans les <strong className="text-sand-200">classements</strong> pour ouvrir son livret. Et
        depuis ton propre bandeau, le chip <em>capitaine</em> mène à ton profil public, celui que les autres voient.
      </Note>
    </Section>
  )
}

function Cabine() {
  return (
    <Section
      id="cabine"
      Icon={CabineIcon}
      title="La cabine du capitaine : compose ton portrait, ton blason, ton titre"
      lead={
        <>
          Ton <strong className="text-sand-200">portrait</strong> peint, ton{' '}
          <strong className="text-sand-200">blason</strong> et un{' '}
          <strong className="text-sand-200">titre équipé</strong> forment l'identité visuelle que tu te composes
          toi-même, visible sur ton <strong className="text-sand-200">livret de bord</strong> (ta page Capitaine)
          comme sur ton <strong className="text-sand-200">profil public</strong>. Tout se règle dans un lieu
          dédié, <strong className="text-sand-200">la cabine du capitaine</strong> (le médaillon ou le lien
          depuis ta page Capitaine).
        </>
      }
    >
      <div className="grid sm:grid-cols-3 gap-4">
        <Mini Icon={CabineIcon} title="Le portrait">
          Sept emplacements à composer&nbsp;: visage, coiffure, pilosité, couvre-chef, accessoire, fond et
          cadre. Un portrait peint, propre à ton capitaine.
        </Mini>
        <Mini Icon={PennantIcon} title="Le blason">
          Ta propre armoirie&nbsp;: forme, emblème, couleurs et champ (l'autre moitié de ton identité,
          affichée à côté du portrait).
        </Mini>
        <Mini Icon={ScrollIcon} title="Le titre équipé">
          Choisis, parmi tous les titres que tu as débloqués, celui qui s'affiche à côté de ton pseudo. Il{' '}
          <strong className="text-sand-200">remplace</strong> l'ancien affichage automatique (le meilleur
          palier de ta série de connexions). Quelques titres ne se gagnent pas&nbsp;: ils sont{' '}
          <strong className="text-sand-200">offerts</strong> pour avoir été là.
        </Mini>
      </div>

      <Note tone="info" Icon={MapIcon} title="Sur la carte et les classements">
        Ton <strong className="text-sand-200">blason</strong> flotte sur ton île sur la carte du monde (à la
        place du pavillon de faction), et le détail d'une île de capitaine montre son{' '}
        <strong className="text-sand-200">portrait</strong>, son blason et son titre. Sur les{' '}
        <strong className="text-sand-200">classements</strong>, chaque capitaine paraît avec son
        mini-portrait.
      </Note>

      <Note tone="info" Icon={ScrollIcon} title="Quatre chemins de déblocage, en plus des items de base">
        Une poignée d'items <strong className="text-sand-200">de base</strong> sont à toi dès le départ. Le
        reste du catalogue se débloque par l'une de <strong className="text-sand-200">quatre voies</strong>&nbsp;:
        les paliers de ta <strong className="text-sand-200">série de connexions</strong> (J7, J14, J30…)&nbsp;;
        chaque <strong className="text-sand-200">rang de réputation</strong> atteint dans une faction (et ce
        rang reste acquis <strong className="text-sand-200">pour de bon</strong>, même si ta réputation
        redescend ensuite)&nbsp;; tes <strong className="text-sand-200">faits d'armes</strong> (objectifs
        accomplis)&nbsp;; et ton <strong className="text-sand-200">prestige cumulé</strong> (50, 200, 500, 1000
        points). À part, quelques titres commémoratifs ne s'obtiennent par aucune de ces voies (voir
        juste en dessous).
      </Note>

      <Note tone="gold" Icon={ScrollIcon} title="Les titres commémoratifs : offerts, jamais gagnés">
        Certains titres ne récompensent pas un exploit, mais une{' '}
        <strong className="text-sand-200">présence</strong>&nbsp;: ils sont offerts pour avoir été là, à un
        moment de l'histoire du jeu qui ne reviendra pas. Le titre «&nbsp;
        <strong className="text-sand-200">Fondateur</strong>&nbsp;» revient ainsi aux capitaines inscrits
        pendant l'alpha (la saison 1)&nbsp;; il ne sera plus jamais attribué à personne d'autre.
      </Note>

      <Note tone="gold" Icon={PrestigeIcon} title="Jamais repris">
        Un élément débloqué n'est <strong className="text-sand-200">jamais repris</strong>&nbsp;: il appartient
        à ton <strong className="text-sand-200">compte</strong>, pas à ta saison de jeu, et{' '}
        <strong className="text-sand-200">survit aux resets de saison</strong>, exactement comme ton
        prestige.
      </Note>

      <Note tone="info" Icon={AnchorIcon} title="Rien d'imposé, tout cosmétique">
        Un capitaine qui n'a rien configuré garde simplement son{' '}
        <strong className="text-sand-200">médaillon de faction</strong> ; aucune apparence ne t'est forcée. Et
        comme le reste de la personnalisation, la cabine est <strong className="text-sand-200">purement
        cosmétique</strong>&nbsp;: aucun bonus de statistiques, de butin ou de vitesse, fidèle à la règle
        anti-pay-to-win.
      </Note>
    </Section>
  )
}

function ClosingNote() {
  return (
    <div className="mt-14 rounded-2xl border border-sand-500/15 bg-ocean-900/30 p-6 text-center">
      <PennantIcon className="w-8 h-8 mx-auto text-sand-300" />
      <p className="font-display text-xl text-sand-300 mt-2">Bon vent, capitaine.</p>
      <p className="text-sm text-ocean-100 mt-1.5 max-w-xl mx-auto">
        Le reste s'apprend en mer. Quand tu seras prêt,{' '}
        <Link to="/map" className="text-sand-300 underline decoration-sand-500/40 underline-offset-2 hover:text-sand-100">
          déploie tes voiles sur la carte du monde
        </Link>.
      </p>
    </div>
  )
}

/* ──────────────────────────────── helpers ────────────────────────────── */

/** Faction-agnostic defense glyph (a fort) — used where the section needs one icon. */
/** Pierre de taille : ce n'est pas une ressource du jeu, donc pas d'icône partagée. */
function StoneIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true" {...p}>
      <path d="M4 20l4-8h8l4 8z" />
      <path d="M9 12l3-6 3 6" />
    </svg>
  )
}

function DefenseShield(p: SVGProps<SVGSVGElement>) {
  return <DefenseIcon type="fort" {...p} />
}

function Mini({
  Icon, title, children,
}: { Icon: Icon; title: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5">
      <div className="flex items-center gap-2.5 mb-2">
        <span className="grid place-items-center w-9 h-9 shrink-0 rounded-lg bg-ocean-900/55 border border-sand-500/25 text-sand-300">
          <Icon className="w-5 h-5" />
        </span>
        <h3 className="font-display text-base text-sand-200 leading-tight">{title}</h3>
      </div>
      <p className="text-sm text-ocean-100 leading-relaxed">{children}</p>
    </div>
  )
}

function ResourceCard({ Icon, name, sub, children }: { Icon: Icon; name: string; sub: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5">
      <div className="flex items-center gap-3 mb-2">
        <span className="grid place-items-center w-11 h-11 shrink-0 rounded-xl bg-ocean-900/55 border border-sand-500/30 text-sand-300">
          <Icon className="w-6 h-6" />
        </span>
        <div>
          <h3 className="font-display text-lg text-sand-300 leading-none">{name}</h3>
          <p className="text-[11px] uppercase tracking-wider text-ocean-300 mt-1">{sub}</p>
        </div>
      </div>
      <p className="text-sm text-ocean-100 leading-relaxed">{children}</p>
    </div>
  )
}

const NOTE_TONES = {
  gold: { box: 'border-sand-500/30 bg-sand-300/[0.06]', icon: 'text-sand-300' },
  info: { box: 'border-ocean-500/35 bg-ocean-700/20', icon: 'text-ocean-300' },
  danger: { box: 'border-red-500/30 bg-red-500/[0.07]', icon: 'text-red-300' },
  wip: { box: 'border-amber-400/30 bg-amber-400/[0.07]', icon: 'text-amber-300' },
} as const

function Note({
  tone = 'info', Icon, title, children,
}: {
  tone?: keyof typeof NOTE_TONES
  Icon: Icon
  title: string
  children: ReactNode
}) {
  const t = NOTE_TONES[tone]
  return (
    <div className={`rounded-2xl border p-5 ${t.box}`}>
      <p className="flex items-center gap-2 font-display text-sand-200">
        <Icon className={`w-5 h-5 shrink-0 ${t.icon}`} />
        {title}
      </p>
      <div className="mt-2 text-sm text-ocean-100 leading-relaxed">{children}</div>
    </div>
  )
}

function Tag({ children, tone = 'plain' }: { children: ReactNode; tone?: 'gold' | 'plain' }) {
  const styles = tone === 'gold'
    ? 'bg-sand-300/15 border-sand-300/40 text-sand-200'
    : 'bg-ocean-900/40 border-sand-500/15 text-ocean-100'
  return <span className={`text-xs px-2.5 py-1 rounded-full border ${styles}`}>{children}</span>
}

function WipTag() {
  return <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300 tracking-normal normal-case">À venir</span>
}

function ObjectiveSample({ title, goal, children }: { title: string; goal: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5">
      <p className="font-display text-base text-sand-200">{title}</p>
      <p className="text-xs text-ocean-300 mt-1 flex items-center gap-1.5">
        <ChevronRight className="w-3.5 h-3.5" /> {goal}
      </p>
      <div className="mt-3 pt-3 border-t border-sand-500/12 text-sm text-ocean-100 flex items-center gap-2 flex-wrap">
        {children}
      </div>
    </div>
  )
}

function Plus() {
  return <span className="text-ocean-300">+</span>
}

function PrestigeReward({ n }: { n: number }) {
  return (
    <span className="inline-flex items-center gap-1 text-sand-200">
      <PrestigeIcon className="w-3.5 h-3.5 text-sand-300" />{n}
    </span>
  )
}
